import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Html, Environment } from '@react-three/drei';
import SafeEnvironment from '../SafeEnvironment';
import * as THREE from 'three';
import {
  ROOM_TYPES, FLOOR_MATERIALS, WALL_MATERIALS, goToPhase, isWallDecorType,
} from '../../store/houseSlice';
import { makeFloorTexture, makeWallTexture, finishToPBR } from '../../utils/textures';
import { edgeControlPoint, edgePointAt } from '../../utils/freeWallRoom';
import { saveOrShareImage, watermarkCanvasToDataUrl } from '../../utils/screenshot';
import { isLowPowerGPU, useMSAA } from '../../utils/gpuProfile';

// Shadow-map edge. Capped at 1024 on phones/tablets, where the depth texture is
// a large slice of the whole GPU budget (see utils/gpuProfile) — but kept at the
// 2048 this view was already tuned for on desktop rather than taking
// shadowMapSize()'s 4096, which would quadruple the cost of a view that was not
// having a problem.
const SHADOW_MAP = isLowPowerGPU() ? 1024 : 2048;
import DownloadModal from '../DownloadModal';
import { useAuth } from '../../context/AuthContext';
import { saveProjectToWeb } from '../../firebase/workspaceService';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useGuestMode } from '../../context/GuestModeContext';
import GuestSaveButton from '../GuestSaveButton';
import Loader3D, { SceneReadySignal } from '../Loader3D';
import { FurnitureMesh3D, TIME_OF_DAY_PRESETS, GLBDoor, GLBWindow, GLBClock, GlbLoadingContext } from '../RoomEditor/ThreeDRoom';
import { ItemIcon } from '../RoomEditor/ItemIcons';
import { useT, Tx } from '../../i18n';
import './House3DView.css';

const FILE_VERSION = 1;

function resolveMat(val, matList) {
  if (!val) return null;
  // Any stored object is already a usable material — a fully custom flat colour
  // (__custom) OR a preset recoloured by hand (__colorOverride). The old check
  // only accepted __custom, so recoloured presets fell through to null and the
  // room rendered with default cream instead of the chosen colour/texture.
  // (Matches ThreeDRoom's resolver so the two 3D views agree.)
  if (typeof val === 'object') return val;
  return matList.find(m => m.id === val) || null;
}

// 1 grid cell = 1 metre — MUST match the per-room view (ThreeDRoom's CELL_M=1)
// so the whole-house view has identical real-world proportions (room sizes vs
// the fixed 2.8 m wall height) and the shared furniture renderer lands at the
// right scale. (Was 2, which made rooms twice as wide as the room view and
// left doors/furniture looking undersized.)
const M        = 1;
// px per grid cell — MUST match Builder.jsx / ThreeDRoom.jsx (both 26). Furniture
// and step-wall opening coords are stored in this space, so a wrong value here
// filters furniture out of its room and places everything at the wrong scale.
const CELL     = 26;
const PX_TO_M  = M / CELL;
const WT       = 0.15;
// Door/window depth in the dollhouse — deliberately MUCH thicker than the wall
// (WT) so openings protrude proud of the wall paint on both faces and read as
// solid, clearly-visible objects. Tune this one value to taste.
const OPENING_DEPTH = 0.4;
// Shift the thick opening toward the room interior (+z local) so its extra depth
// projects INTO the room and its exterior face sits flush with the wall's outer
// face, instead of sticking out the exterior side.
const OPENING_INSET = 0.05 ;

// Same painting photo the room view mounts on "painting" wall decor — so the
// dollhouse shows the real artwork, not a blank canvas.
const PAINTING_IMAGE_URL = '/media/wall_painting_1.png';

// Grid cell size for per-area wall/floor painting (~1 metre) — MUST match the
// room view's WALL_CELL_M so patches land on the same cells the user painted.
const WALL_CELL_M = 1.0;

// Load an image into a THREE texture (mirrors ThreeDRoom.useImageTexture) so the
// dollhouse paintings render the actual photo instead of a plain canvas rect.
function useImageTexture(url) {
  const [texture, setTexture] = useState(null);
  useEffect(() => {
    if (!url) { setTexture(null); return; }
    let cancelled = false;
    const loader = new THREE.TextureLoader();
    loader.load(url, (tex) => {
      if (cancelled) return;
      tex.colorSpace = THREE.SRGBColorSpace;
      setTexture(tex);
    });
    return () => { cancelled = true; };
  }, [url]);
  return texture;
}

// A recoloured preset (__colorOverride) can also carry a hand-picked finish —
// apply it as a roughness/metalness override on top of the pattern texture the
// preset's own generator drew. (Ports ThreeDRoom's RoomScene helper so the two
// 3D views resolve per-area materials identically.)
const withFinishOverride = (mat, data) =>
  (data && mat?.finish && mat.__colorOverride) ? { ...data, ...finishToPBR(mat.finish) } : data;

// ~1m grid for a surface of the given width/height, and the local-space centre
// of one "col-row" cell within it — identical maths to the room view so a patch
// keyed "c-r" lands on the same physical cell here as it did when painted.
function faceGrid(width, height) {
  const cols = Math.max(1, Math.round(width / WALL_CELL_M));
  const rows = Math.max(1, Math.round(height / WALL_CELL_M));
  return { cols, rows, cellW: width / cols, cellH: height / rows };
}
function cellCenter(key, width, height) {
  const { cols, rows, cellW, cellH } = faceGrid(width, height);
  const [c, r] = key.split('-').map(Number);
  const cc = Math.min(cols - 1, Math.max(0, c));
  const rr = Math.min(rows - 1, Math.max(0, r));
  return { x: -width / 2 + (cc + 0.5) * cellW, y: -height / 2 + (rr + 0.5) * cellH, cellW, cellH };
}

// Perimeter wall segments of a compound (merged / non-rectangular) room, with the
// SAME per-segment keys the room view paints against (h_top_…, v_left_…). Ported
// verbatim from ThreeDRoom.buildCompoundWallSegs (CELL_M === M === 1), so a wall
// face painted in the room view can be matched to its geometry in the dollhouse.
// cx/cz are relative to the room's bounding-box centre (metres); rot orients the
// segment's interior-facing plane (+z local → room interior).
function buildCompoundWallSegs(room) {
  const rects = room.rects && room.rects.length > 1
    ? room.rects : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
  const cells = new Set();
  for (const r of rects)
    for (let row = r.y; row < r.y + r.h; row++)
      for (let col = r.x; col < r.x + r.w; col++)
        cells.add(`${col},${row}`);
  const W = room.w * M, D = room.h * M;
  const topF = {}, botF = {}, leftF = {}, rightF = {};
  for (const key of cells) {
    const [col, row] = key.split(',').map(Number);
    const lc = col - room.x, lr = row - room.y;
    if (!cells.has(`${col},${row - 1}`)) (topF[lr]   = topF[lr]   || []).push(lc);
    if (!cells.has(`${col},${row + 1}`)) (botF[lr]   = botF[lr]   || []).push(lc);
    if (!cells.has(`${col - 1},${row}`)) (leftF[lc]  = leftF[lc]  || []).push(lr);
    if (!cells.has(`${col + 1},${row}`)) (rightF[lc] = rightF[lc] || []).push(lr);
  }
  const mergeH = (map, side) => {
    const out = [];
    for (const [rStr, cols] of Object.entries(map)) {
      const row = Number(rStr);
      const sorted = [...cols].sort((a, b) => a - b);
      let s = sorted[0], l = sorted[0];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === l + 1) l = sorted[i];
        else { out.push({ row, sc: s, ec: l + 1, side }); s = l = sorted[i]; }
      }
      out.push({ row, sc: s, ec: l + 1, side });
    }
    return out;
  };
  const mergeV = (map, side) => {
    const out = [];
    for (const [cStr, rows] of Object.entries(map)) {
      const col = Number(cStr);
      const sorted = [...rows].sort((a, b) => a - b);
      let s = sorted[0], l = sorted[0];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === l + 1) l = sorted[i];
        else { out.push({ col, sr: s, er: l + 1, side }); s = l = sorted[i]; }
      }
      out.push({ col, sr: s, er: l + 1, side });
    }
    return out;
  };
  const walls = [];
  for (const seg of [...mergeH(topF, 'top'), ...mergeH(botF, 'bottom')]) {
    const segW = (seg.ec - seg.sc) * M;
    const cx   = seg.sc * M - W / 2 + segW / 2;
    const cz   = seg.side === 'top' ? seg.row * M - D / 2 : (seg.row + 1) * M - D / 2;
    walls.push({ cx, cz, segW, rot: seg.side === 'top' ? [0, 0, 0] : [0, Math.PI, 0], key: `h_${seg.side}_${seg.row}_${seg.sc}` });
  }
  for (const seg of [...mergeV(leftF, 'left'), ...mergeV(rightF, 'right')]) {
    const segD = (seg.er - seg.sr) * M;
    const cx   = seg.side === 'left' ? seg.col * M - W / 2 : (seg.col + 1) * M - W / 2;
    const cz   = seg.sr * M - D / 2 + segD / 2;
    walls.push({ cx, cz, segW: segD, rot: seg.side === 'left' ? [0, Math.PI / 2, 0] : [0, -Math.PI / 2, 0], key: `v_${seg.side}_${seg.col}_${seg.sr}` });
  }
  return walls;
}

// Renders a compound room's per-segment wall paint (full-face `wallFaces[key]`
// + per-cell `wallPatches[key]`) onto the correct perimeter-wall geometry — the
// piece the dollhouse's rectangle-split rooms couldn't reach, because compound
// paint is keyed by segment (h_top_…/v_left_…), not back/front/left/right.
function CompoundWallOverlays({ room, design, H }) {
  const segs = useMemo(() => buildCompoundWallSegs(room), [room]);
  const bbCx = room.x * M + room.w * M / 2;
  const bbCz = room.y * M + room.h * M / 2;

  const faceInfo = useMemo(() => {
    const wf = design?.wallFaces || {};
    const out = {};
    for (const k in wf) { const m = resolveMat(wf[k], WALL_MATERIALS); if (m) out[k] = { data: withFinishOverride(m, makeWallTexture(m)), color: m.color || m.baseColor || '#f0e8d8' }; }
    return out;
  }, [design?.wallFaces]);
  const patchInfo = useMemo(() => {
    const wp = design?.wallPatches || {};
    const out = {};
    for (const f in wp) { out[f] = {}; for (const k in wp[f]) { const m = resolveMat(wp[f][k], WALL_MATERIALS); if (m) out[f][k] = { data: withFinishOverride(m, makeWallTexture(m)), color: m.color || m.baseColor || '#f0e8d8' }; } }
    return out;
  }, [design?.wallPatches]);

  const cellMat = (info, cellW, cellH) => {
    const data = info.data, color = info.color;
    const repX = Math.max(1, Math.round(cellW * (data?.repeat?.[0] ?? 0.4)));
    const repY = Math.max(0.15, (data?.repeat?.[1] ?? 0.5) * (cellH / H));
    let map = null;
    if (data?.texture) {
      if (!data.repeat) map = data.texture;
      else { const t = data.texture.clone(); t.repeat.set(repX, repY); t.needsUpdate = true; map = t; }
    }
    const r = data?.roughness ?? 0.82;
    return (
      <meshStandardMaterial
        color={data?.texture ? '#ffffff' : color} map={map}
        roughness={r} metalness={data?.metalness ?? 0}
        envMapIntensity={r < 0.35 ? 0.7 : 0.12}
        side={THREE.DoubleSide}
      />
    );
  };

  return (
    <group>
      {segs.map(w => {
        const faceMat = faceInfo[w.key];
        const patches = patchInfo[w.key] || {};
        const patchKeys = Object.keys(patches);
        if (!faceMat && patchKeys.length === 0) return null;
        const rotY = w.rot[1];
        // Nudge toward the room interior (+z local) far enough to clear a
        // shared-wall colour panel (~0.088 m in) as well as a plain structural
        // wall, so the paint is never occluded whichever kind of wall it lands on.
        const d = 0.095;
        const px = bbCx + w.cx + Math.sin(rotY) * d;
        const pz = bbCz + w.cz + Math.cos(rotY) * d;
        return (
          <group key={w.key} position={[px, H / 2, pz]} rotation={w.rot}>
            {faceMat && (
              <mesh position={[0, 0, 0.004]}>
                <planeGeometry args={[w.segW, H]} />
                {cellMat(faceMat, w.segW, H)}
              </mesh>
            )}
            {patchKeys.map(key => {
              const { x, y, cellW, cellH } = cellCenter(key, w.segW, H);
              return (
                <mesh key={key} position={[x, y, 0.006]}>
                  <planeGeometry args={[cellW, cellH]} />
                  {cellMat(patches[key], cellW, cellH)}
                </mesh>
              );
            })}
          </group>
        );
      })}
    </group>
  );
}

// House-view light intensities per time of day. The sky/exposure/env come from
// the shared TIME_OF_DAY_PRESETS (so day/night reads the same as the room view),
// but the whole-house dollhouse lights a lot more surface than a single room, so
// these intensities are tuned lower than the room view's to avoid blowing walls
// out — while the per-room ceiling fill rises at night so interiors stay legible.
const HOUSE_LIGHT = {
  // env kept low: the whole-house dollhouse is far more exposed to the image
  // environment than a single enclosed room, so the room view's env intensities
  // would wash every wall with the HDRI's tint (dawn = pink). The mood comes
  // from the warm sun/ambient/point-lights instead; env is just for reflections.
  morning: { amb: 0.34, hemi: 0.42, sun: 1.15, fill: 0.42, pt: 4.5, env: 0.13 },
  evening: { amb: 0.30, hemi: 0.34, sun: 1.00, fill: 0.34, pt: 6.0, env: 0.18 },
  night:   { amb: 0.10, hemi: 0.14, sun: 0.16, fill: 0.12, pt: 11,  env: 0.08 },
};

// toneMappingExposure is a renderer property (the Canvas `gl` prop only sets it
// once), so drive it reactively from inside the scene as the time of day changes.
function ExposureControl({ exposure }) {
  const { gl } = useThree();
  gl.toneMappingExposure = exposure;
  return null;
}

const DOOR_W   = 0.88;
const DOOR_H   = 2.06;
const WIN_W    = 0.98;
const WIN_H    = 0.88;
const WIN_SILL = 0.9;

// Door style ids that are GLB models (door1…door21) — rendered via the shared
// GLBDoor; anything else is a legacy procedural style handled below.
const GLB_DOOR_STYLES = new Set(Array.from({ length: 21 }, (_, i) => `door${i + 1}`));
const GLB_WINDOW_STYLES = new Set(Array.from({ length: 15 }, (_, i) => `window${i + 1}`));
// Wall clocks are GLB now too (clock1…clock13) — rendered via the shared GLBClock.
const GLB_CLOCK_STYLES = new Set(Array.from({ length: 13 }, (_, i) => `clock${i + 1}`));

// ─── WALL SEGMENTATION (handles doors + windows) ──────────────────────────────
// Splits a wall of length wallW into solid box pieces around all openings.
// Returns [{along, w, h, y}] where along = centre from wall-start.

function wallPieces(wallW, openings, H) {
  if (!openings || openings.length === 0) {
    return [{ along: wallW / 2, w: wallW, h: H, y: H / 2 }];
  }

  const doors = openings.filter(o => o.type === 'door' || o.type === 'archway').map(o => {
    const ow = o.width  || (o.type === 'archway' ? 1.2 : DOOR_W);
    const oh = o.height || (o.type === 'archway' ? 2.4 : DOOR_H);
    return {
      xl: Math.max(0, o.pos * wallW - ow / 2),
      xr: Math.min(wallW, o.pos * wallW + ow / 2),
      yb: 0,
      yt: oh,
    };
  });
  const wins = openings.filter(o => o.type === 'window').map(o => {
    const ow   = o.width  || WIN_W;
    const oh   = o.height || WIN_H;
    const sill = o.sill   || WIN_SILL;
    return {
      xl:  Math.max(0, o.pos * wallW - ow / 2),
      xr:  Math.min(wallW, o.pos * wallW + ow / 2),
      yb:  sill,
      yt:  sill + oh,
    };
  });

  // X breakpoints from every opening edge
  const xSet = new Set([0, wallW]);
  [...doors, ...wins].forEach(v => { xSet.add(v.xl); xSet.add(v.xr); });
  const xs = Array.from(xSet).sort((a, b) => a - b);

  // Y breakpoints from each opening's vertical bounds
  const ySet = new Set([0, H]);
  doors.forEach(d => ySet.add(d.yt));
  wins.forEach(w => { ySet.add(w.yb); ySet.add(w.yt); });
  const ys = Array.from(ySet).sort((a, b) => a - b);

  const pieces = [];
  for (let xi = 0; xi < xs.length - 1; xi++) {
    const xl = xs[xi], xr = xs[xi + 1];
    const colW = xr - xl;
    if (colW < 0.001) continue;

    const colDoors = doors.filter(d => xl >= d.xl - 0.001 && xr <= d.xr + 0.001);
    const colWins  = wins.filter(w  => xl >= w.xl  - 0.001 && xr <= w.xr  + 0.001);

    for (let yi = 0; yi < ys.length - 1; yi++) {
      const yb = ys[yi], yt = ys[yi + 1];
      const cellH = yt - yb;
      if (cellH < 0.001) continue;

      const cutByDoor = colDoors.some(d => yb < d.yt - 0.001);
      const cutByWin  = colWins.some(w  => yb >= w.yb - 0.001 && yt <= w.yt + 0.001);
      if (cutByDoor || cutByWin) continue;

      pieces.push({ along: (xl + xr) / 2, w: colW, h: cellH, y: yb + cellH / 2 });
    }
  }
  return pieces;
}

// Like wallPieces but skips ranges shared with lower-ID neighbours (partial adjacency).
// skipRanges: [{start, end}] in metres along the wall (0..wallLen).
function partialWallPieces(wallLen, openings, skipRanges, H) {
  if (!skipRanges || skipRanges.length === 0) return wallPieces(wallLen, openings, H);

  const sorted = [...skipRanges].sort((a, b) => a.start - b.start);
  const renderRanges = [];
  let cursor = 0;
  for (const { start, end } of sorted) {
    if (start - cursor > 0.001) renderRanges.push([cursor, start]);
    if (end > cursor) cursor = end;
  }
  if (wallLen - cursor > 0.001) renderRanges.push([cursor, wallLen]);
  if (renderRanges.length === 0) return [];

  const result = [];
  for (const [rs, re] of renderRanges) {
    const segLen = re - rs;
    const segOps = openings
      .filter(op => {
        const oCenter = op.pos * wallLen;
        const oW = op.type === 'door'    ? (op.width || DOOR_W)
                 : op.type === 'archway' ? (op.width || 1.2)
                 :                         (op.width || WIN_W);
        return oCenter - oW / 2 >= rs - 0.001 && oCenter + oW / 2 <= re + 0.001;
      })
      .map(op => ({ ...op, pos: (op.pos * wallLen - rs) / segLen }));
    for (const p of wallPieces(segLen, segOps, H)) {
      result.push({ ...p, along: p.along + rs });
    }
  }
  return result;
}

// Returns shared-wall entries for walls covered by lower-ID neighbours.
// Each entry: {start, end, adjRoom, adjWall} where start/end are metres along this wall.
function adjSharedWalls(allRooms, room, side, myOrigId) {
  const isSameCompound = r => getOrigId(r.id) === myOrigId;
  const isLowerOrig    = r => !isSameCompound(r) && getOrigId(r.id) < myOrigId;
  if (side === 'top') {
    return allRooms
      .filter(r => r.id !== room.id && isLowerOrig(r) && r.y + r.h === room.y && r.x < room.x + room.w && r.x + r.w > room.x)
      .map(r => ({ start: Math.max(0, r.x - room.x) * M, end: Math.min(room.w, r.x + r.w - room.x) * M, adjRoom: r, adjWall: 'bottom' }));
  }
  if (side === 'bottom') {
    return allRooms
      .filter(r => r.id !== room.id && isLowerOrig(r) && r.y === room.y + room.h && r.x < room.x + room.w && r.x + r.w > room.x)
      .map(r => ({ start: Math.max(0, r.x - room.x) * M, end: Math.min(room.w, r.x + r.w - room.x) * M, adjRoom: r, adjWall: 'top' }));
  }
  if (side === 'left') {
    return allRooms
      .filter(r => r.id !== room.id && isLowerOrig(r) && r.x + r.w === room.x && r.y < room.y + room.h && r.y + r.h > room.y)
      .map(r => ({ start: Math.max(0, r.y - room.y) * M, end: Math.min(room.h, r.y + r.h - room.y) * M, adjRoom: r, adjWall: 'right' }));
  }
  return allRooms
    .filter(r => r.id !== room.id && isLowerOrig(r) && r.x === room.x + room.w && r.y < room.y + room.h && r.y + r.h > room.y)
    .map(r => ({ start: Math.max(0, r.y - room.y) * M, end: Math.min(room.h, r.y + r.h - room.y) * M, adjRoom: r, adjWall: 'left' }));
}

// Same shape as adjSharedWalls, but for the stretch of wall bordering a DRAWN
// room's rasterised cells (the `drawnBlockers` from getWallSkipRanges). Always
// wins — a drawn room's own outline always draws the physical wall there — so
// this room needs its own colour panel over that stretch or it's just a hole
// through to the drawn room's wall behind (which is what "the little room took
// the free room's colour" actually was: no colour was leaking, the room's own
// wall was skipped with nothing put in its place). `openings: []` on the stub
// adjRoom is correct — a drawn room's openings use edge+t, not wall+pos, so they
// can't be remapped here; the room's OWN door on this side still cuts the panel
// via sharedPanelPieces' `ownSegOps`, independent of the neighbour.
function drawnSharedWalls(drawnBlockers, room, side) {
  if (side === 'top') {
    return drawnBlockers
      .filter(r => r.y + r.h === room.y && r.x < room.x + room.w && r.x + r.w > room.x)
      .map(r => ({ start: Math.max(0, r.x - room.x) * M, end: Math.min(room.w, r.x + r.w - room.x) * M, adjRoom: { ...r, openings: [] }, adjWall: 'bottom' }));
  }
  if (side === 'bottom') {
    return drawnBlockers
      .filter(r => r.y === room.y + room.h && r.x < room.x + room.w && r.x + r.w > room.x)
      .map(r => ({ start: Math.max(0, r.x - room.x) * M, end: Math.min(room.w, r.x + r.w - room.x) * M, adjRoom: { ...r, openings: [] }, adjWall: 'top' }));
  }
  if (side === 'left') {
    return drawnBlockers
      .filter(r => r.x + r.w === room.x && r.y < room.y + room.h && r.y + r.h > room.y)
      .map(r => ({ start: Math.max(0, r.y - room.y) * M, end: Math.min(room.h, r.y + r.h - room.y) * M, adjRoom: { ...r, openings: [] }, adjWall: 'right' }));
  }
  return drawnBlockers
    .filter(r => r.x === room.x + room.w && r.y < room.y + room.h && r.y + r.h > room.y)
    .map(r => ({ start: Math.max(0, r.y - room.y) * M, end: Math.min(room.h, r.y + r.h - room.y) * M, adjRoom: { ...r, openings: [] }, adjWall: 'left' }));
}

// Thin interior face panels so each room shows its OWN wall colour on shared boundaries.
// Panel sits just OUTSIDE the lower-ID neighbour's wall box (deeper into the room),
// so it is closer to this room's camera and wins the depth test.
const PANEL_T = 0.012;
const PANEL_INSET = WT / 2 + PANEL_T / 2 + 0.001; // = 0.082m from boundary into this room

function sharedPanelPieces(shared, room, side, wallColor, allRooms, H) {
  const rx = room.x * M, rz = room.y * M;
  const W = room.w * M, D = room.h * M;
  const panels = [];

  for (const sw of shared) {
    const { start, end, adjRoom, adjWall } = sw;
    const segLen = end - start;

    // Remap adjacent room's openings on the facing wall into this segment's local space
    const isHorizAdj = adjWall === 'left' || adjWall === 'right';
    const adjWallLen = isHorizAdj ? adjRoom.h * M : adjRoom.w * M;
    const adjWallOrigin = isHorizAdj ? adjRoom.y * M : adjRoom.x * M;
    const thisWallOrigin = (side === 'left' || side === 'right') ? rz : rx;

    // Step-wall openings live only in rect_0; collect them from all compound rects.
    const adjCompOrigId = getOrigId(adjRoom.id);
    const seenPanelIds = new Set();
    const adjStepOps = (allRooms || [])
      .filter(cr => getOrigId(cr.id) === adjCompOrigId && cr.id !== adjRoom.id)
      .flatMap(cr => (cr.openings || []).filter(o => o.wall === adjWall && o.absX !== undefined))
      .filter(o => { if (seenPanelIds.has(o.id)) return false; seenPanelIds.add(o.id); return true; });

    const segOps = [
      ...(adjRoom.openings || []).filter(o => o.wall === adjWall),
      ...adjStepOps,
    ]
      .map(o => {
        let oCenter;
        if (o.absX !== undefined) {
          const orx2 = (adjRoom.origX != null ? adjRoom.origX : adjRoom.x) * M;
          const orz2 = (adjRoom.origY != null ? adjRoom.origY : adjRoom.y) * M;
          oCenter = isHorizAdj ? orz2 + o.absY * (M / CELL) : orx2 + o.absX * (M / CELL);
        } else {
          oCenter = adjWallOrigin + o.pos * adjWallLen;
        }
        return { ...o, pos: (oCenter - thisWallOrigin - start) / segLen };
      })
      .filter(o => {
        const ow = o.type === 'door' ? (o.width || DOOR_W) : o.type === 'archway' ? (o.width || 1.2) : (o.width || WIN_W);
        return o.pos * segLen + ow / 2 > 0.001 && o.pos * segLen - ow / 2 < segLen - 0.001;
      });

    // Also cut for THIS room's own openings on this side — needed when this room has
    // higher ID and its door isn't visible to the lower-ID adjacent room's wall logic.
    const ownWallLen = (side === 'top' || side === 'bottom') ? W : D;
    const orx = (room.origX != null ? room.origX : room.x) * M;
    const orz = (room.origY != null ? room.origY : room.y) * M;
    const ownSegOps = (room.openings || [])
      .filter(o => o.wall === side)
      .map(o => {
        if (o.absX !== undefined) {
          const isH2 = side === 'top' || side === 'bottom';
          const gCenter = isH2 ? orx + o.absX * (M / CELL) : orz + o.absY * (M / CELL);
          const p = ((isH2 ? gCenter - rx : gCenter - rz) - start) / segLen;
          return { ...o, pos: p };
        }
        return { ...o, pos: (o.pos * ownWallLen - start) / segLen };
      })
      .filter(o => {
        const ow = o.type === 'door' ? (o.width || DOOR_W) : o.type === 'archway' ? (o.width || 1.2) : (o.width || WIN_W);
        return o.pos * segLen + ow / 2 > 0.001 && o.pos * segLen - ow / 2 < segLen - 0.001;
      });

    for (const p of wallPieces(segLen, [...segOps, ...ownSegOps], H)) {
      if (side === 'right') {
        panels.push({ x: rx + W - PANEL_INSET, y: p.y, z: rz + start + p.along, w: PANEL_T, h: p.h, d: p.w });
      } else if (side === 'left') {
        panels.push({ x: rx + PANEL_INSET, y: p.y, z: rz + start + p.along, w: PANEL_T, h: p.h, d: p.w });
      } else if (side === 'top') {
        panels.push({ x: rx + start + p.along, y: p.y, z: rz + PANEL_INSET, w: p.w, h: p.h, d: PANEL_T });
      } else { // bottom
        panels.push({ x: rx + start + p.along, y: p.y, z: rz + D - PANEL_INSET, w: p.w, h: p.h, d: PANEL_T });
      }
    }
  }
  return panels;
}

// Convenience: skip ranges array from shared wall entries
function skipRangesFrom(shared) {
  return shared.map(sw => ({ start: sw.start, end: sw.end }));
}

// Returns openings from adjacent rooms at the given wall side, remapped to [0..1] pos space.
// Handles the case where the adjacent room has higher or lower ID — ID-agnostic.
function getAdjOpenings(allRooms, room, side) {
  const rx = room.x * M, rz = room.y * M;
  const W = room.w * M, D = room.h * M;
  let cands = [];
  if (side === 'top') {
    cands = allRooms
      .filter(r => r.id !== room.id && Math.abs((r.y + r.h) * M - rz) < 0.02 && r.x * M < rx + W - 0.01 && (r.x + r.w) * M > rx + 0.01)
      .map(r => ({ r, adjWall: 'bottom', origin: r.x * M, len: r.w * M, thisOrigin: rx, thisLen: W }));
  } else if (side === 'bottom') {
    cands = allRooms
      .filter(r => r.id !== room.id && Math.abs(r.y * M - (rz + D)) < 0.02 && r.x * M < rx + W - 0.01 && (r.x + r.w) * M > rx + 0.01)
      .map(r => ({ r, adjWall: 'top', origin: r.x * M, len: r.w * M, thisOrigin: rx, thisLen: W }));
  } else if (side === 'left') {
    cands = allRooms
      .filter(r => r.id !== room.id && Math.abs((r.x + r.w) * M - rx) < 0.02 && r.y * M < rz + D - 0.01 && (r.y + r.h) * M > rz + 0.01)
      .map(r => ({ r, adjWall: 'right', origin: r.y * M, len: r.h * M, thisOrigin: rz, thisLen: D }));
  } else {
    cands = allRooms
      .filter(r => r.id !== room.id && Math.abs(r.x * M - (rx + W)) < 0.02 && r.y * M < rz + D - 0.01 && (r.y + r.h) * M > rz + 0.01)
      .map(r => ({ r, adjWall: 'left', origin: r.y * M, len: r.h * M, thisOrigin: rz, thisLen: D }));
  }
  const result = [];
  for (const { r, adjWall, origin, len, thisOrigin, thisLen } of cands) {
    // Step-wall openings are assigned only to rect_0 in expandedRooms, but the matching
    // virtual rect may be rect_1 (or later). Scan all rects of the same compound room.
    const compoundOrigId = getOrigId(r.id);
    const seenIds = new Set();
    const opsToCheck = [
      ...(r.openings || []).filter(o => o.wall === adjWall),
      ...allRooms
        .filter(cr => getOrigId(cr.id) === compoundOrigId && cr.id !== r.id)
        .flatMap(cr => (cr.openings || []).filter(o => o.wall === adjWall && o.absX !== undefined)),
    ].filter(o => { if (seenIds.has(o.id)) return false; seenIds.add(o.id); return true; });

    for (const op of opsToCheck) {
      let center;
      if (op.absX !== undefined) {
        // Step-wall opening: derive world center from pixel coords, not from pos*len
        const orx2 = (r.origX != null ? r.origX : r.x) * M;
        const orz2 = (r.origY != null ? r.origY : r.y) * M;
        center = (adjWall === 'top' || adjWall === 'bottom')
          ? orx2 + op.absX * (M / CELL)
          : orz2 + op.absY * (M / CELL);
      } else {
        center = origin + op.pos * len;
      }
      const newPos = (center - thisOrigin) / thisLen;
      if (newPos > 0.001 && newPos < 0.999) {
        result.push({ ...op, pos: newPos });
      }
    }
  }
  return result;
}

// Returns ranges on a given side that should be left open (no wall rendered here).
// Opens passages to: (a) other rects of the SAME compound room (same origId),
// and (b) neighbours with a LOWER origId (they render the wall, we skip ours to avoid doubles).
// Neighbours with a HIGHER origId: this room renders the wall.
function getOrigId(id) {
  return typeof id === 'string' ? Number(id.split('_')[0]) : id;
}
function getWallSkipRanges(allRooms, room, side, myOrigId) {
  const rx = room.x * M, rz = room.y * M;
  const W = room.w * M, D = room.h * M;
  let cands;
  if (side === 'top') {
    cands = allRooms.filter(r => r.id !== room.id && Math.abs((r.y + r.h) * M - rz) < 0.02 && r.x * M < rx + W - 0.01 && (r.x + r.w) * M > rx + 0.01);
  } else if (side === 'bottom') {
    cands = allRooms.filter(r => r.id !== room.id && Math.abs(r.y * M - (rz + D)) < 0.02 && r.x * M < rx + W - 0.01 && (r.x + r.w) * M > rx + 0.01);
  } else if (side === 'left') {
    cands = allRooms.filter(r => r.id !== room.id && Math.abs((r.x + r.w) * M - rx) < 0.02 && r.y * M < rz + D - 0.01 && (r.y + r.h) * M > rz + 0.01);
  } else {
    cands = allRooms.filter(r => r.id !== room.id && Math.abs(r.x * M - (rx + W)) < 0.02 && r.y * M < rz + D - 0.01 && (r.y + r.h) * M > rz + 0.01);
  }
  const toSkip = cands.filter(r => {
    // A DRAWN room renders its own walls (FreeRoom3D) and is not part of the
    // rectangle split — so a rect room abutting one drew a SECOND wall in the
    // same place, in its own colour, and the two fought for the surface. These
    // blockers always win: the drawn room owns that stretch of wall.
    if (r._drawnRoom) return true;
    const rOrig = getOrigId(r.id);
    return rOrig === myOrigId || rOrig < myOrigId;
  });
  if (side === 'top' || side === 'bottom') {
    return toSkip.map(r => ({ start: Math.max(0, r.x * M - rx), end: Math.min(W, (r.x + r.w) * M - rx) }));
  }
  return toSkip.map(r => ({ start: Math.max(0, r.y * M - rz), end: Math.min(D, (r.y + r.h) * M - rz) }));
}

// Lighten (+) or darken (-) a hex colour by a fraction (0..1)
function shadeColor(hex, amount) {
  const n = parseInt((hex || '#888888').replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (n >> 16) + Math.round(255 * amount)));
  const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + Math.round(255 * amount)));
  const b = Math.min(255, Math.max(0, (n & 0xff) + Math.round(255 * amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// ─── DOOR + WINDOW GEOMETRY (in wall-local space) ────────────────────────────

function HouseOpening3D({ opening, room }) {
  const { type, wall, pos } = opening;
  // Real painting photo (async) — same asset the room view uses. Hook must run
  // unconditionally (before the door/window/arch early returns below).
  const paintingTex = useImageTexture(type === 'painting' ? PAINTING_IMAGE_URL : null);
  const rx = room.x * M, rz = room.y * M;
  const W  = room.w * M, D  = room.h * M;
  const orx = (room.origX != null ? room.origX : room.x) * M;
  const orz = (room.origY != null ? room.origY : room.y) * M;

  // Pre-build the arch ring shape so ExtrudeGeometry has clean closed ends (no open torus gaps)
  const archRingShape = useMemo(() => {
    if (type !== 'archway') return null;
    const dW = opening.width || 1.2;
    const archR = dW / 2;
    const fW = 0.08;
    const s = new THREE.Shape();
    s.moveTo(archR + fW, 0);
    s.absarc(0, 0, archR + fW, 0, Math.PI, false); // outer upper arc: right→top→left
    s.lineTo(-(archR - fW), 0);                      // bridge to inner-left
    s.absarc(0, 0, archR - fW, Math.PI, 0, true);   // inner upper arc: left→top→right
    s.lineTo(archR + fW, 0);                          // close
    return s;
  }, [type, opening.width]);

  let gx, gz, rotY;
  if (opening.absX !== undefined && opening.absY !== undefined) {
    // Step-wall opening: pixel coords relative to original compound room BB → world metres
    gx = orx + opening.absX * (M / CELL);
    gz = orz + opening.absY * (M / CELL);
    if      (wall === 'top')    rotY = 0;
    else if (wall === 'bottom') rotY = Math.PI;
    else if (wall === 'left')   rotY = Math.PI / 2;
    else                        rotY = -Math.PI / 2;
  } else if (wall === 'top')    { gx = rx + pos * W; gz = rz;     rotY = 0;            }
  else if (wall === 'bottom') { gx = rx + pos * W; gz = rz + D; rotY = Math.PI;      }
  else if (wall === 'left')   { gx = rx;           gz = rz + pos * D; rotY = Math.PI / 2;  }
  else                        { gx = rx + W;        gz = rz + pos * D; rotY = -Math.PI / 2; }

  if (type === 'door') {
    // Match the room view's door rendering 1:1 — same GLB (fallback door1),
    // same default size (0.86 × 2.05) and, crucially, the same default panel
    // colour (#e8e0d4, not the old tan #c4a87a) so an un-recoloured door reads
    // identically in both views. `tint` recolours the actual GLB leaf when the
    // door was given a colour; `color` is only the loading-slab fallback.
    const dW = opening.width  || 0.86;
    const dH = opening.height || 2.05;
    const doorStyle = GLB_DOOR_STYLES.has(opening.style) ? opening.style : 'door1';
    const panelColor = opening.color || '#e8e0d4';
    return (
      <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
        {/* Thick door (much deeper than the wall), shifted into the room so the
            depth projects inward rather than out the exterior side. */}
        <group position={[0, 0, OPENING_INSET]}>
          <GLBDoor styleId={doorStyle} dW={dW} dH={dH} color={panelColor} tint={opening.colored ? (opening.color || null) : null} depthMin={OPENING_DEPTH} />
        </group>
      </group>
    );
  }

  if (type === 'archway') {
    const dW    = opening.width  || 1.2;
    const dH    = opening.height || 2.4;
    const archR = dW / 2;
    const rectH = dH - archR;
    const fW = 0.08, fD = 0.13;
    const frameColor = opening.color || '#e0d8c8';
    return (
      <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
        {/* left jamb — floor to arch base */}
        <mesh position={[-(archR + fW / 2), rectH / 2, fD / 2]}>
          <boxGeometry args={[fW, rectH, fD]} />
          <meshStandardMaterial color={frameColor} roughness={0.55} side={THREE.DoubleSide} />
        </mesh>
        {/* right jamb */}
        <mesh position={[archR + fW / 2, rectH / 2, fD / 2]}>
          <boxGeometry args={[fW, rectH, fD]} />
          <meshStandardMaterial color={frameColor} roughness={0.55} side={THREE.DoubleSide} />
        </mesh>
        {/* arch ring — solid extruded shape, no open tube ends */}
        <mesh position={[0, rectH, 0]}>
          <extrudeGeometry args={[archRingShape, { depth: fD, bevelEnabled: false }]} />
          <meshStandardMaterial color={frameColor} roughness={0.55} side={THREE.DoubleSide} />
        </mesh>
      </group>
    );
  }

  if (type === 'window') {
    // Windows are GLB models now (window1…) — a legacy/unknown style id falls
    // back to window1.
    const wW    = opening.width  || WIN_W;
    const wH    = opening.height || WIN_H;
    const wSill = opening.sill   || WIN_SILL;
    const winStyle = GLB_WINDOW_STYLES.has(opening.style) ? opening.style : 'window1';
    return (
      <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
        <group position={[0, 0, OPENING_INSET]}>
          <GLBWindow styleId={winStyle} wW={wW} wH={wH} wSill={wSill} tint={opening.colored ? (opening.color || null) : null} depthMin={OPENING_DEPTH} />
        </group>
      </group>
    );
  }

  // ── Wall decor (painting / AC / clock / outlet / radiator) ──────────────────
  // Surface-mounted on the interior wall face at +z local (same convention as
  // the door/window meshes above). These never cut the wall — RoomBox already
  // excludes them from the wall-piece cutting. Simplified but recognizable at
  // the whole-house overview scale.
  const DECOR_DIM = {
    painting: { w: 0.80, h: 0.60, sill: 1.30 },
    ac:       { w: 0.85, h: 0.30, sill: 2.15 },
    clock:    { w: 0.35, h: 0.35, sill: 1.55 },
    outlet:   { w: 0.10, h: 0.10, sill: 0.30 },
    radiator: { w: 1.00, h: 0.50, sill: 0.12 },
  };
  if (DECOR_DIM[type]) {
    const dd   = DECOR_DIM[type];
    const w    = opening.width  || dd.w;
    const h    = opening.height || dd.h;
    const sill = opening.sill != null ? opening.sill : dd.sill;
    const midY = sill + h / 2;
    const zf   = WT / 2 + 0.01; // just proud of the interior wall surface

    if (type === 'painting') {
      const frame = opening.color || '#5a4326';
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          <mesh position={[0, midY, zf + 0.02]} castShadow>
            <boxGeometry args={[w + 0.06, h + 0.06, 0.04]} />
            <meshStandardMaterial color={frame} roughness={0.55} />
          </mesh>
          <mesh position={[0, midY, zf + 0.041]}>
            <boxGeometry args={[w, h, 0.006]} />
            <meshStandardMaterial map={paintingTex} color={paintingTex ? '#ffffff' : '#d9cdb8'} roughness={0.85} />
          </mesh>
        </group>
      );
    }
    if (type === 'ac') {
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          <mesh position={[0, midY, zf + 0.09]} castShadow>
            <boxGeometry args={[w, h, 0.18]} />
            <meshStandardMaterial color={opening.color || '#f4f4f0'} roughness={0.5} />
          </mesh>
          <mesh position={[0, midY - h * 0.28, zf + 0.185]}>
            <boxGeometry args={[w * 0.9, h * 0.16, 0.01]} />
            <meshStandardMaterial color="#c8ccd0" roughness={0.7} />
          </mesh>
        </group>
      );
    }
    if (type === 'clock') {
      // Clocks are GLB models now (clock1…) — same models as the room view; a
      // legacy/unknown style id falls back to clock1.
      const clockStyle = GLB_CLOCK_STYLES.has(opening.style) ? opening.style : 'clock1';
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          <GLBClock styleId={clockStyle} cSize={w} midY={midY} zBase={zf} color={opening.color} tint={opening.colored ? (opening.color || null) : null} />
        </group>
      );
    }

    if (type === 'outlet') {
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          <mesh position={[0, midY, zf + 0.008]}>
            <boxGeometry args={[w, h, 0.016]} />
            <meshStandardMaterial color={opening.color || '#f0efe9'} roughness={0.6} />
          </mesh>
          {[-w * 0.16, w * 0.16].map((ox, i) => (
            <mesh key={i} position={[ox, midY, zf + 0.017]}>
              <boxGeometry args={[0.012, 0.03, 0.006]} />
              <meshStandardMaterial color="#8a8a86" />
            </mesh>
          ))}
        </group>
      );
    }
    if (type === 'radiator') {
      const body = opening.color || '#eceae4';
      const fins = Math.max(4, Math.round(w / 0.12));
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          <mesh position={[0, midY, zf + 0.05]} castShadow>
            <boxGeometry args={[w, h, 0.09]} />
            <meshStandardMaterial color={body} roughness={0.55} />
          </mesh>
          {Array.from({ length: fins }, (_, i) => {
            const fx = -w / 2 + (i + 0.5) * (w / fins);
            return (
              <mesh key={i} position={[fx, midY, zf + 0.098]}>
                <boxGeometry args={[0.012, h * 0.86, 0.03]} />
                <meshStandardMaterial color={shadeColor(body, -0.08)} roughness={0.6} />
              </mesh>
            );
          })}
        </group>
      );
    }
  }

  return null;
}

// ─── FURNITURE IN WORLD SPACE ─────────────────────────────────────────────────

function FurnitureWorld({ item, timeOfDay = 'morning' }) {
  const rot      = item.rotation || 0;
  const displayW = rot % 180 === 0 ? item.w : item.h;
  const displayH = rot % 180 === 0 ? item.h : item.w;

  // Absolute world position of the item's centre, at the house view's 1 m/cell
  // scale (now identical to the per-room view).
  const posX = (item.x + displayW / 2) * PX_TO_M;
  const posZ = (item.y + displayH / 2) * PX_TO_M;

  // Reuse the room editor's exact furniture renderer so the whole-house view is
  // 1:1 with the per-room view — the GitHub GLB models (sofas / armchairs /
  // dining tables) plus the procedural chairs, beds, lamps, plants, rugs, etc.
  // Position is supplied as an absolute-world override; roomW/roomD are huge so
  // FurnitureMesh3D's own in-room culling never drops a valid item. The group's
  // Y lifts items stacked on top of another (e.g. a plant on a cabinet), matching
  // the per-room view's elevation.
  return (
    <group position={[0, item.elevation || 0, 0]}>
      <FurnitureMesh3D
        item={item}
        room={{ x: 0, y: 0, w: 1, h: 1 }}
        roomW={1e6}
        roomD={1e6}
        posXOverride={posX}
        posZOverride={posZ}
        timeOfDay={timeOfDay}
      />
    </group>
  );
}

// Converts step-wall openings (absX/absY) to pos-based entries for wallPieces cutting.
// Scans ALL virtual rects of the same compound room so openings stored in rect_0 can
// cut holes in walls that are actually rendered by rect_1 (or vice-versa).
function stepOpsAsPos(allRooms, room, wallSide) {
  const myOrigId = getOrigId(room.id);
  const rx = room.x * M, rz = room.y * M;
  const W  = room.w * M, D  = room.h * M;
  const isH       = wallSide === 'top' || wallSide === 'bottom';
  const wallCoord = wallSide === 'bottom' ? rz + D
                  : wallSide === 'top'    ? rz
                  : wallSide === 'right'  ? rx + W
                  :                         rx;
  // origX/origY are the same for all virtual rects of the same compound room
  const orx = (room.origX != null ? room.origX : room.x) * M;
  const orz = (room.origY != null ? room.origY : room.y) * M;

  return allRooms
    .filter(r => getOrigId(r.id) === myOrigId)
    .flatMap(r => (r.openings || []).filter(o => o.absX !== undefined && o.wall === wallSide))
    .map(o => {
      const gx = orx + o.absX * (M / CELL);
      const gz = orz + o.absY * (M / CELL);
      // Only include if this opening actually lands on this rect's wall
      if (isH ? Math.abs(gz - wallCoord) > 0.05 : Math.abs(gx - wallCoord) > 0.05) return null;
      const pos = isH ? (gx - rx) / W : (gz - rz) / D;
      return { ...o, pos };
    })
    .filter(o => o != null && o.pos > 0.001 && o.pos < 0.999);
}

// ─── ROOM BOX ─────────────────────────────────────────────────────────────────

function RoomBox({ room, design, furniture, allRooms, drawnBlockers = [], H, showLabel = true, labelPos = null, timeOfDay = 'morning' }) {
  const rx = room.x * M, rz = room.y * M;
  const W  = room.w * M, D  = room.h * M;
  const cx = rx + W / 2, cz = rz + D / 2;
  const myOrigId = getOrigId(room.id);

  const rt         = ROOM_TYPES.find(r => r.type === room.type);
  const floorMat   = resolveMat(design?.floor, FLOOR_MATERIALS);
  const wallMat    = resolveMat(design?.wall,  WALL_MATERIALS);
  const floorColor = floorMat?.baseColor || '#d4c4a8';
  // Default MUST match the room view's RoomScene fallback (#f0e8d8) — a different
  // default here made every wall without an explicit material read as a slightly
  // greyer colour in the dollhouse than it did in the room view.
  const wallColor  = wallMat?.color      || '#f0e8d8';

  const floorData = useMemo(() => makeFloorTexture(floorMat), [floorMat?.id, floorMat?.baseColor, floorMat?.finish]);
  const wallData  = useMemo(() => makeWallTexture(wallMat),   [wallMat?.id,  wallMat?.color,     wallMat?.finish]);

  const floorTex  = floorData?.texture ?? null;
  const floorRough = floorData?.roughness ?? 0.85;
  const floorMetal = floorData?.metalness ?? 0;

  // BB-edge openings pierce the rect's outer walls; step-wall openings render at absX/absY only
  const ops     = (room.openings || []).filter(o => o.absX === undefined);
  const stepOps = (room.openings || []).filter(o => o.absX !== undefined);
  // Only structural openings (door/window/archway) cut a hole in the wall. Wall
  // decor (painting/AC/clock/outlet/radiator) mounts ON the surface, so it must
  // be excluded from the wall-piece cutting below — otherwise a painting would
  // punch a rectangular gap through the wall behind it.
  const cutOps      = ops.filter(o => !isWallDecorType(o.type));
  const stepCut     = (side) => stepOpsAsPos(allRooms, room, side).filter(o => !isWallDecorType(o.type));
  const topOps    = cutOps.filter(o => o.wall === 'top');
  const bottomOps = cutOps.filter(o => o.wall === 'bottom');
  const leftOps   = cutOps.filter(o => o.wall === 'left');
  const rightOps  = cutOps.filter(o => o.wall === 'right');

  // Open passages only to same-compound rects; render walls facing different rooms.
  // Lower-origId room renders the structural wall; higher-origId skips to avoid z-fighting
  // but renders a thin colour panel so each room shows ITS OWN wall colour on shared faces.
  const topPcs    = partialWallPieces(W, [...topOps,    ...stepCut('top'),    ...getAdjOpenings(allRooms, room, 'top')],    getWallSkipRanges([...allRooms, ...drawnBlockers], room, 'top',    myOrigId), H);
  const botPcs    = partialWallPieces(W, [...bottomOps, ...stepCut('bottom'), ...getAdjOpenings(allRooms, room, 'bottom')], getWallSkipRanges([...allRooms, ...drawnBlockers], room, 'bottom', myOrigId), H);
  const leftPcs   = partialWallPieces(D, [...leftOps,   ...stepCut('left'),   ...getAdjOpenings(allRooms, room, 'left')],   getWallSkipRanges([...allRooms, ...drawnBlockers], room, 'left',   myOrigId), H);
  const rightPcs  = partialWallPieces(D, [...rightOps,  ...stepCut('right'),  ...getAdjOpenings(allRooms, room, 'right')],  getWallSkipRanges([...allRooms, ...drawnBlockers], room, 'right',  myOrigId), H);

  const topShared   = [...adjSharedWalls(allRooms, room, 'top',    myOrigId), ...drawnSharedWalls(drawnBlockers, room, 'top')];
  const botShared   = [...adjSharedWalls(allRooms, room, 'bottom', myOrigId), ...drawnSharedWalls(drawnBlockers, room, 'bottom')];
  const leftShared  = [...adjSharedWalls(allRooms, room, 'left',   myOrigId), ...drawnSharedWalls(drawnBlockers, room, 'left')];
  const rightShared = [...adjSharedWalls(allRooms, room, 'right',  myOrigId), ...drawnSharedWalls(drawnBlockers, room, 'right')];
  const allPanels   = [
    ...sharedPanelPieces(topShared,   room, 'top',    wallColor, allRooms, H),
    ...sharedPanelPieces(botShared,   room, 'bottom', wallColor, allRooms, H),
    ...sharedPanelPieces(leftShared,  room, 'left',   wallColor, allRooms, H),
    ...sharedPanelPieces(rightShared, room, 'right',  wallColor, allRooms, H),
  ];

  // wall material: apply canvas texture when available, else plain color
  const wallTexClone = useMemo(() => {
    if (!wallData?.texture) return null;
    const rScale = wallData.repeat?.[0] ?? 0.4;
    const rY     = wallData.repeat?.[1] ?? 0.5;
    const repX   = Math.max(1, Math.round(Math.max(W, D) * rScale));
    const t = wallData.texture.clone();
    t.repeat.set(repX, rY);
    t.needsUpdate = true;
    return t;
  }, [wallData, W, D]);

  const wRough = wallData?.roughness ?? 0.82;
  const wMat = (
    <meshStandardMaterial
      color={wallData?.texture ? '#ffffff' : wallColor}
      map={wallTexClone}
      roughness={wRough}
      metalness={wallData?.metalness ?? 0}
      envMapIntensity={wRough < 0.35 ? 0.7 : 0.12}
    />
  );

  // ── Per-area material overlays (parity with the room view) ──────────────────
  // Resolve stored floor/wall patch + full-face ids → textured materials exactly
  // as ThreeDRoom does, so the dollhouse shows the same painted cells and
  // full-face overrides the user set per room.
  const floorPatchInfo = useMemo(() => {
    const fp = design?.floorPatches || {};
    const out = {};
    for (const k in fp) {
      const m = resolveMat(fp[k], FLOOR_MATERIALS);
      if (m) out[k] = { data: withFinishOverride(m, makeFloorTexture(m)), color: m.baseColor || m.color || '#d4c4a8' };
    }
    return out;
  }, [design?.floorPatches]);
  const wallPatchInfo = useMemo(() => {
    const wp = design?.wallPatches || {};
    const out = {};
    for (const f in wp) {
      out[f] = {};
      for (const k in wp[f]) {
        const m = resolveMat(wp[f][k], WALL_MATERIALS);
        if (m) out[f][k] = { data: withFinishOverride(m, makeWallTexture(m)), color: m.color || m.baseColor || '#f0e8d8' };
      }
    }
    return out;
  }, [design?.wallPatches]);
  // Whole-wall accent material. NOTE: the design field is `wallFaces` (what the
  // room view's setWallFaceMaterial reducer writes and RoomScene reads) — NOT
  // `wallFaceMats`. Reading the wrong key here is exactly why a full-wall
  // material change never showed in the dollhouse.
  const wallFaceInfo = useMemo(() => {
    const wf = design?.wallFaces || {};
    const out = {};
    for (const k in wf) {
      const m = resolveMat(wf[k], WALL_MATERIALS);
      if (m) out[k] = { data: withFinishOverride(m, makeWallTexture(m)), color: m.color || m.baseColor || '#f0e8d8' };
    }
    return out;
  }, [design?.wallFaces]);

  // Lit per-cell FLOOR material — shows the cell's slice of the full-room tiling
  // (repeat/offset) so tile size matches the base floor and adjacent cells stay
  // seamless. Matches the room view's floorCellMat.
  const floorCellMat = (info, x, y, cellW, cellH) => {
    const data = info.data;
    const r = data?.roughness ?? 0.85;
    let map = null;
    if (data?.texture) {
      const t = data.texture.clone();
      const bx = data.texture.repeat.x || 1, by = data.texture.repeat.y || 1;
      t.repeat.set(bx * cellW / W, by * cellH / D);
      t.offset.set(bx * (x - cellW / 2 + W / 2) / W, by * (y - cellH / 2 + D / 2) / D);
      t.needsUpdate = true; map = t;
    }
    return (
      <meshStandardMaterial
        map={map}
        color={data?.texture ? '#ffffff' : info.color}
        roughness={r}
        metalness={data?.metalness ?? 0}
        envMapIntensity={r < 0.12 ? 1.8 : r < 0.30 ? 1.0 : r < 0.60 ? 0.45 : 0.15}
      />
    );
  };
  // Lit per-cell WALL material (also used for a full-face override plane). It's
  // physically offset in front of the wall/panel (see the face insets below), so
  // it does NOT use polygonOffset — a depth bias would also push it in front of
  // the doors and wall items that mount just proud of the wall, covering them.
  const wallCellMat = (info, cellW, cellH) => {
    const data = info.data, color = info.color;
    const repX = Math.max(1, Math.round(cellW * (data?.repeat?.[0] ?? 0.4)));
    const repY = Math.max(0.15, (data?.repeat?.[1] ?? 0.5) * (cellH / H));
    let map = null;
    if (data?.texture) {
      if (!data.repeat) map = data.texture;
      else { const t = data.texture.clone(); t.repeat.set(repX, repY); t.needsUpdate = true; map = t; }
    }
    const r = data?.roughness ?? 0.82;
    return (
      <meshStandardMaterial
        color={data?.texture ? '#ffffff' : color}
        map={map}
        roughness={r}
        metalness={data?.metalness ?? 0}
        envMapIntensity={r < 0.35 ? 0.7 : 0.12}
        side={THREE.DoubleSide}
      />
    );
  };

  // The 4 wall faces, mapped to the room view's face ids. Overlays sit just proud
  // of each interior wall surface. `present` skips fully-open shared walls (no
  // wall there to paint on) so a patch never floats in an open doorway gap.
  //
  // Inset (distance from the room-boundary line into the room) depends on what's
  // on that side: a SHARED wall (this room is the higher-id one) draws a thin
  // opaque colour panel at PANEL_INSET, so the style overlay must sit IN FRONT of
  // that panel or it gets occluded and the interior wall reads as unstyled. A
  // plain structural wall (outer or lower-id side) only needs to clear WT/2.
  const PANEL_CLEAR = PANEL_INSET + PANEL_T / 2 + 0.006; // in front of the panel
  const WALL_CLEAR  = WT / 2 + 0.002;                    // hug the wall (keeps
  // doors/items, which mount a bit further proud, visible in front of the paint)
  const insetFor = sharedLen => (sharedLen ? PANEL_CLEAR : WALL_CLEAR);
  const wallFaces = [
    { id: 'back',  width: W, present: topPcs.length   || topShared.length,   pos: [cx, H / 2, rz + insetFor(topShared.length)],       rot: [0, 0, 0] },
    { id: 'front', width: W, present: botPcs.length   || botShared.length,   pos: [cx, H / 2, rz + D - insetFor(botShared.length)],   rot: [0, Math.PI, 0] },
    { id: 'left',  width: D, present: leftPcs.length  || leftShared.length,  pos: [rx + insetFor(leftShared.length), H / 2, cz],      rot: [0, Math.PI / 2, 0] },
    { id: 'right', width: D, present: rightPcs.length || rightShared.length, pos: [rx + W - insetFor(rightShared.length), H / 2, cz], rot: [0, -Math.PI / 2, 0] },
  ];

  return (
    <group>
      {/* Floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, 0.005, cz]} receiveShadow>
        <planeGeometry args={[W, D]} />
        <meshStandardMaterial
          color={floorTex ? '#ffffff' : floorColor}
          map={floorTex}
          roughness={floorRough}
          metalness={floorMetal}
          envMapIntensity={floorRough < 0.12 ? 1.8 : floorRough < 0.30 ? 1.0 : floorRough < 0.60 ? 0.45 : 0.15}
        />
      </mesh>

      {/* Floor per-area patches — same rotated frame as the floor, at room centre */}
      {Object.keys(floorPatchInfo).length > 0 && (
        <group position={[cx, 0, cz]} rotation={[-Math.PI / 2, 0, 0]}>
          {Object.keys(floorPatchInfo).map(key => {
            const { x, y, cellW, cellH } = cellCenter(key, W, D);
            return (
              <mesh key={`fp-${key}`} position={[x, y, 0.008]}>
                <planeGeometry args={[cellW, cellH]} />
                {floorCellMat(floorPatchInfo[key], x, y, cellW, cellH)}
              </mesh>
            );
          })}
        </group>
      )}

      {/* Wall full-face overrides + per-area patches */}
      {wallFaces.map(f => {
        const faceMat = wallFaceInfo[f.id];
        const patches = wallPatchInfo[f.id] || {};
        const patchKeys = Object.keys(patches);
        // Render whenever this face has paint. (Dropped the `present` gate: for a
        // simple room all 4 faces are real walls, and a compound room's open
        // edges are keyed differently so they never carry 'back/front/left/right'
        // paint anyway — so `present` could only ever wrongly hide a real
        // interior wall, never usefully skip one.)
        if (!faceMat && patchKeys.length === 0) return null;
        return (
          <group key={`wo-${f.id}`} position={f.pos} rotation={f.rot}>
            {faceMat && (
              <mesh position={[0, 0, 0.004]}>
                <planeGeometry args={[f.width, H]} />
                {wallCellMat(faceMat, f.width, H)}
              </mesh>
            )}
            {patchKeys.map(key => {
              const { x, y, cellW, cellH } = cellCenter(key, f.width, H);
              return (
                <mesh key={`wp-${key}`} position={[x, y, 0.006]}>
                  <planeGeometry args={[cellW, cellH]} />
                  {wallCellMat(patches[key], cellW, cellH)}
                </mesh>
              );
            })}
          </group>
        );
      })}

      {/* Outer walls — shared boundaries are fully open (open-plan connect) */}
      {topPcs.map((p, i)   => <mesh key={`t${i}`} position={[rx + p.along, p.y, rz]}     castShadow receiveShadow><boxGeometry args={[p.w, p.h, WT]} />{wMat}</mesh>)}
      {botPcs.map((p, i)   => <mesh key={`b${i}`} position={[rx + p.along, p.y, rz + D]} castShadow receiveShadow><boxGeometry args={[p.w, p.h, WT]} />{wMat}</mesh>)}
      {leftPcs.map((p, i)  => <mesh key={`l${i}`} position={[rx,      p.y, rz + p.along]} castShadow receiveShadow><boxGeometry args={[WT, p.h, p.w]} />{wMat}</mesh>)}
      {rightPcs.map((p, i) => <mesh key={`r${i}`} position={[rx + W,  p.y, rz + p.along]} castShadow receiveShadow><boxGeometry args={[WT, p.h, p.w]} />{wMat}</mesh>)}

      {/* Interior colour panels on shared walls — this room's colour on its own side */}
      {allPanels.map((p, i) => (
        <mesh key={`panel_${i}`} position={[p.x, p.y, p.z]}>
          <boxGeometry args={[p.w, p.h, p.d]} />
          {wMat}
        </mesh>
      ))}

      {/* Doors, Windows & Arches (BB-edge and step-wall) */}
      {/* KEYED BY WHERE IT IS, not by id alone. `room.openings` can carry
          "extras" merged in from a neighbouring room's shared wall (see where
          extrasByIdx is built), and those keep the id they had on the other
          room — so the same auto-wall opening reaching a room from two sides
          appeared twice under one key. React warned about duplicate keys and
          was entitled to drop one of them.
          Wall and position make it unique without inventing an id, and two
          openings genuinely at the same spot on the same wall would be the
          same opening anyway. */}
      {[...ops, ...stepOps].map(op => (
        <HouseOpening3D key={`${op.id}:${op.wall ?? ''}:${op.pos ?? ''}`} opening={op} room={room} />
      ))}

      {/* Furniture.
          DEDUPED BY ID. A compound room is assembled from several rectangles
          and the same piece can be collected once per rectangle it touches, so
          this list could hold the same item twice — two children under one key,
          which React warns about and is entitled to resolve by dropping one.
          Filtering is better than making the key unique: a duplicate is not
          just a key clash, it is the same model drawn twice in the same place. */}
      {furniture
        .filter((item, i, all) => all.findIndex(o => o.id === item.id) === i)
        .map(item => (
          <FurnitureWorld key={item.id} item={item} timeOfDay={timeOfDay} />
        ))}

      {/* per-room ceiling light (no visible fixture) — a soft warm fill so
          interiors don't go flat; brighter at night so rooms stay legible */}
      <pointLight position={[cx, H * 0.92, cz]} intensity={HOUSE_LIGHT[timeOfDay].pt} color="#fff3e0" distance={Math.max(W, D) * 2.6} decay={2} />

      {/* Room label — compound rooms use centroid, simple rooms use cell centre */}
      {showLabel && (
        <Html center position={[labelPos ? labelPos[0] : cx, H * 0.52, labelPos ? labelPos[1] : cz]} distanceFactor={14} zIndexRange={[10, 0]}>
          <div className="h3v-room-label">{rt?.icon} {room.label}</div>
        </Html>
      )}
    </group>
  );
}

// ─── STANDALONE WALL SEGMENT ─────────────────────────────────────────────────
// Renders a single user-drawn wall segment (from Draw Walls mode) as a 3D box.
// x1/y1/x2/y2 are grid intersection coordinates (same scale as room x/y).

function WallSegment3D({ seg, H, wallColor = '#dcd9d4', mat = null, iwPatches = null }) {
  const isH = seg.y1 === seg.y2;
  const ox = Math.min(seg.x1, seg.x2) * M;
  const oz = Math.min(seg.y1, seg.y2) * M;
  const wallLen = isH ? Math.abs(seg.x2 - seg.x1) * M : Math.abs(seg.y2 - seg.y1) * M;
  const openings = useMemo(() => (seg.openings || []).map(o => ({ ...o, pos: o.t })), [seg.openings]);
  const pieces   = useMemo(() => wallPieces(wallLen, openings, H), [wallLen, openings, H]);
  const fakeRoom = useMemo(() => isH
    ? { x: Math.min(seg.x1, seg.x2), y: seg.y1, w: Math.abs(seg.x2 - seg.x1), h: 0 }
    : { x: seg.x1, y: Math.min(seg.y1, seg.y2), w: 0, h: Math.abs(seg.y2 - seg.y1) },
    [seg.x1, seg.y1, seg.x2, seg.y2, isH]);

  // Carry the containing room's actual wall MATERIAL (texture + finish), not just
  // a flat colour, so a drawn partition reads the same as it does in the room
  // view instead of looking like a plain grey slab in the dollhouse.
  const wallData = useMemo(() => withFinishOverride(mat, makeWallTexture(mat)), [mat?.id, mat?.color, mat?.finish]);
  const segColor = mat?.color || wallColor;
  const wallTexClone = useMemo(() => {
    if (!wallData?.texture) return null;
    const rScale = wallData.repeat?.[0] ?? 0.4;
    const rY     = wallData.repeat?.[1] ?? 0.5;
    const repX   = Math.max(1, Math.round(wallLen * rScale));
    const t = wallData.texture.clone();
    t.repeat.set(repX, rY);
    t.needsUpdate = true;
    return t;
  }, [wallData, wallLen]);
  const wRough = wallData?.roughness ?? 0.82;

  // ── Per-side interior-wall paint (iw-<segId>-a / -b) ──────────────────────
  // The room view lets you paint EACH face of a drawn wall; the per-cell paint
  // lives on the containing room's design.wallPatches under these keys. Resolve
  // + render the same cells on each face here so a painted drawn wall matches.
  const iwInfo = useMemo(() => {
    const resolveSide = (raw) => {
      const out = {};
      for (const cell in (raw || {})) {
        const m = resolveMat(raw[cell], WALL_MATERIALS);
        if (m) out[cell] = { data: withFinishOverride(m, makeWallTexture(m)), color: m.color || m.baseColor || '#f0e8d8' };
      }
      return out;
    };
    return { a: resolveSide(iwPatches?.a), b: resolveSide(iwPatches?.b) };
  }, [iwPatches]);
  const centerX = isH ? ox + wallLen / 2 : ox;
  const centerZ = isH ? oz : oz + wallLen / 2;
  const IWOFF = WT / 2 + 0.002; // hug the wall so the door in front stays visible
  const iwFaces = [
    { patches: iwInfo.a, pos: isH ? [centerX, H / 2, centerZ + IWOFF] : [centerX + IWOFF, H / 2, centerZ], rot: isH ? [0, 0, 0] : [0, Math.PI / 2, 0] },
    { patches: iwInfo.b, pos: isH ? [centerX, H / 2, centerZ - IWOFF] : [centerX - IWOFF, H / 2, centerZ], rot: isH ? [0, Math.PI, 0] : [0, -Math.PI / 2, 0] },
  ].filter(f => Object.keys(f.patches).length > 0);
  const iwCellMat = (info, cellW, cellH) => {
    const data = info.data, color = info.color;
    const repX = Math.max(1, Math.round(cellW * (data?.repeat?.[0] ?? 0.4)));
    const repY = Math.max(0.15, (data?.repeat?.[1] ?? 0.5) * (cellH / H));
    let map = null;
    if (data?.texture) {
      if (!data.repeat) map = data.texture;
      else { const t = data.texture.clone(); t.repeat.set(repX, repY); t.needsUpdate = true; map = t; }
    }
    const r = data?.roughness ?? 0.82;
    return (
      <meshStandardMaterial
        color={data?.texture ? '#ffffff' : color} map={map}
        roughness={r} metalness={data?.metalness ?? 0}
        envMapIntensity={r < 0.35 ? 0.7 : 0.12}
        side={THREE.DoubleSide}
      />
    );
  };

  return (
    <group>
      {pieces.map((p, i) => (
        <mesh key={i}
          position={isH ? [ox + p.along, p.y, oz] : [ox, p.y, oz + p.along]}
          castShadow receiveShadow>
          <boxGeometry args={isH ? [p.w, p.h, WT] : [WT, p.h, p.w]} />
          <meshStandardMaterial
            color={wallData?.texture ? '#ffffff' : segColor}
            map={wallTexClone}
            roughness={wRough}
            metalness={wallData?.metalness ?? 0}
            envMapIntensity={wRough < 0.35 ? 0.7 : 0.12}
          />
        </mesh>
      ))}
      {openings.map(op => {
        // A wall segment has two usable faces. Wall decor stores which one it's
        // on in op.side (±1); map that to the wall label HouseOpening3D uses so
        // the item mounts on the SAME face the user placed it (top/left = the +z
        // /+x face, bottom/right = the opposite face). Structural openings have
        // no side (symmetric) and fall through to top/left.
        const wall = isH
          ? (op.side === -1 ? 'bottom' : 'top')
          : (op.side === -1 ? 'right'  : 'left');
        return <HouseOpening3D key={op.id} opening={{ ...op, wall }} room={fakeRoom} />;
      })}
      {iwFaces.map((f, fi) => (
        <group key={`iw-${fi}`} position={f.pos} rotation={f.rot}>
          {Object.keys(f.patches).map(key => {
            const { x, y, cellW, cellH } = cellCenter(key, wallLen, H);
            return (
              <mesh key={key} position={[x, y, 0.006]}>
                <planeGeometry args={[cellW, cellH]} />
                {iwCellMat(f.patches[key], cellW, cellH)}
              </mesh>
            );
          })}
        </group>
      ))}
    </group>
  );
}

// The wall material of the room a standalone segment belongs to. A drawn wall
// renders in the room view with the room's own wall material, so the dollhouse
// must resolve the SAME room the same way:
//   • OVERLAP test (not midpoint-inside) — identical to the room view's own
//     per-room segment filter (sx1 < rx2 && sx2 > rx1 && …). A wall drawn on/near
//     a room edge overlaps the room but its midpoint can fall outside it, which a
//     midpoint test would miss → the wall then fell back to a default material
//     and didn't match the room view.
//   • designs keyed by origId (what RoomBox uses), with the raw id as fallback.
// Returns the resolved material object, or null if the segment isn't in a room.
function segRoomMat(seg, rooms, designs) {
  const sx1 = Math.min(seg.x1, seg.x2), sx2 = Math.max(seg.x1, seg.x2);
  const sy1 = Math.min(seg.y1, seg.y2), sy2 = Math.max(seg.y1, seg.y2);
  const rectsOf = (r) => (r.rects && r.rects.length > 1) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
  const matOf = (r) => resolveMat((designs[getOrigId(r.id)] || designs[r.id])?.wall, WALL_MATERIALS);

  // A wall that runs along the boundary between two rooms OVERLAPS both of them,
  // so taking the first overlapping room in list order handed it whichever room
  // happened to be created first — often the neighbour's colour, not its own.
  // Decide by the wall's MIDDLE instead: the room whose floor that point sits on
  // is the room the wall belongs to. Curved walls use their arc middle, since a
  // bowed wall's chord midpoint can fall in the wrong room entirely.
  const m = seg.bulge
    ? edgePointAt({ x: seg.x1, y: seg.y1, bulge: seg.bulge }, { x: seg.x2, y: seg.y2 }, 0.5)
    : { x: (seg.x1 + seg.x2) / 2, y: (seg.y1 + seg.y2) / 2 };
  // Strictly inside first, so a wall sitting exactly ON a shared boundary (in
  // neither room's interior) doesn't win the tolerant pass by list order.
  for (const eps of [-0.02, 0.02]) {
    for (const r of rooms) {
      const inside = rectsOf(r).some(rc =>
        m.x >= rc.x - eps && m.x <= rc.x + rc.w + eps &&
        m.y >= rc.y - eps && m.y <= rc.y + rc.h + eps);
      if (!inside) continue;
      const wm = matOf(r);
      if (wm) return wm;
    }
  }

  // Nothing contains it (a wall drawn in open space, or one just outside every
  // room) — fall back to any room it merely overlaps.
  for (const r of rooms) {
    for (const rc of rectsOf(r)) {
      const overlaps = sx1 < rc.x + rc.w + 0.02 && sx2 > rc.x - 0.02 && sy1 < rc.y + rc.h + 0.02 && sy2 > rc.y - 0.02;
      if (overlaps) {
        const wm = matOf(r);
        if (wm) return wm;
      }
    }
  }
  return null;
}

// Colour + full material wrappers for WallSegment3D. Colour falls back to a
// neutral grey for a segment drawn in open space that isn't inside any room.
function segWallColor(seg, rooms, designs) {
  const wm = segRoomMat(seg, rooms, designs);
  return wm?.color || '#dcd9d4';
}
function segWallMaterial(seg, rooms, designs) {
  return segRoomMat(seg, rooms, designs);
}

// Per-cell paint applied to a drawn wall's two faces in the room view is stored
// on WHICHEVER room's design was being edited, under keys iw-<segId>-a / -b.
// Scan every design so the paint is found no matter which room owns it.
function segIwPatches(seg, designs) {
  const out = {};
  for (const k in (designs || {})) {
    const wp = designs[k]?.wallPatches;
    if (!wp) continue;
    if (wp[`iw-${seg.id}-a`]) out.a = wp[`iw-${seg.id}-a`];
    if (wp[`iw-${seg.id}-b`]) out.b = wp[`iw-${seg.id}-b`];
  }
  return out;
}

// ─── FREE-FORM WALL (any angle + curve) ──────────────────────────────────────
// Renders a freeWalls entry: a straight one as a single rotated box, a curved
// one (bulge≠0) as a chain of short boxes sampled along its quadratic arc.
// `mat` is the containing room's wall material — without it a free wall showed
// a flat colour while every other wall in the room showed its texture.
function FreeWall3D({ wall, H, color = '#dcd9d4', mat = null, openings = [] }) {
  const x1 = wall.x1 * M, z1 = wall.y1 * M;
  const x2 = wall.x2 * M, z2 = wall.y2 * M;
  const bulge = (wall.bulge || 0) * M;
  const thick = (wall.thickness || 0.16) * M;
  const pts = useMemo(() => {
    const dx = x2 - x1, dz = z2 - z1;
    const len = Math.hypot(dx, dz) || 1;
    const nx = dz / len, nz = -dx / len;                 // perp unit (+bulge side)
    const cx = (x1 + x2) / 2 + nx * bulge * 2;
    const cz = (z1 + z2) / 2 + nz * bulge * 2;           // bezier control point
    const N = Math.abs(bulge) > 0.02 ? 20 : 1;
    const out = [];
    for (let i = 0; i <= N; i++) {
      const t = i / N, mt = 1 - t;
      out.push({
        x: mt * mt * x1 + 2 * mt * t * cx + t * t * x2,
        z: mt * mt * z1 + 2 * mt * t * cz + t * t * z2,
      });
    }
    return out;
  }, [x1, z1, x2, z2, bulge]);

  // Total (sampled) length, so each tessellated piece knows its t-range and can
  // be cut where an opening sits: a door leaves nothing (or a header above), a
  // window leaves a sill below + a header above.
  const total = useMemo(() => {
    let L = 0;
    for (let i = 0; i < pts.length - 1; i++) L += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].z - pts[i].z);
    return L || 1;
  }, [pts]);

  const wallData = useMemo(() => withFinishOverride(mat, makeWallTexture(mat)), [mat?.id, mat?.color, mat?.finish]);
  const baseColor = mat?.color || color;
  // Each tessellated piece is its own box with 0..1 UVs, so it carries only its
  // OWN share of the repeat — the whole wall's count crammed into every piece
  // is what made the pattern microscopic before.
  const pieceMat = (len, h) => {
    let map = null;
    if (wallData?.texture) {
      if (!wallData.repeat) map = wallData.texture;
      else {
        const t = wallData.texture.clone();
        t.repeat.set(Math.max(0.01, len * (wallData.repeat[0] ?? 0.4)),
                     Math.max(0.05, (wallData.repeat[1] ?? 0.5) * (h / H)));
        t.needsUpdate = true; map = t;
      }
    }
    // metalness/envMapIntensity were left at their THREE defaults (0 / 1.0) —
    // every other wall material in this file sets both explicitly, tempering
    // envMapIntensity down for anything but a rough finish. Left at 1.0, a
    // smooth/gloss finish reflects the scene's env map at full strength with no
    // light source behind the camera to catch, which reads as solid black.
    const rough = wallData?.roughness ?? 0.82;
    return (
      <meshStandardMaterial
        color={map ? '#ffffff' : baseColor} map={map}
        roughness={rough} metalness={wallData?.metalness ?? 0}
        envMapIntensity={rough < 0.35 ? 0.7 : 0.12}
      />
    );
  };

  return (
    <group>
      {(() => {
        const meshes = [];
        // Cumulative arc length at each sample, so an opening's `t` (a CURVE
        // parameter, not a fraction of length) maps to a real distance along
        // the wall — on a bowed wall those two differ.
        const cum = [0];
        for (let i = 0; i < pts.length - 1; i++) {
          cum.push(cum[i] + Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].z - pts[i].z));
        }
        const NSEG = pts.length - 1;
        const wallLen = cum[NSEG];
        const arcAtT = (tB) => {
          const f = Math.max(0, Math.min(1, tB)) * NSEG;
          const i = Math.min(NSEG - 1, Math.floor(f));
          return cum[i] + (cum[i + 1] - cum[i]) * (f - i);
        };
        const ptAtS = (s) => {
          const c = Math.max(0, Math.min(wallLen, s));
          let i = 1;
          while (i < cum.length - 1 && cum[i] < c) i++;
          const f = (c - cum[i - 1]) / ((cum[i] - cum[i - 1]) || 1);
          return {
            x: pts[i - 1].x + (pts[i].x - pts[i - 1].x) * f,
            z: pts[i - 1].z + (pts[i].z - pts[i - 1].z) * f,
          };
        };
        // Wall between two arc lengths at a height band, split at every sample
        // boundary so a bowed wall stays bowed.
        const emitSpan = (s0, s1, yc, h, key) => {
          if (h <= 0.02 || s1 - s0 <= 0.005) return;
          const bounds = [s0];
          for (let i = 1; i < NSEG; i++) if (cum[i] > s0 + 1e-4 && cum[i] < s1 - 1e-4) bounds.push(cum[i]);
          bounds.push(s1);
          for (let k = 0; k < bounds.length - 1; k++) {
            const a0 = ptAtS(bounds[k]), a1 = ptAtS(bounds[k + 1]);
            const len = Math.hypot(a1.x - a0.x, a1.z - a0.z);
            if (len < 1e-4) continue;
            meshes.push(
              <mesh key={`${key}-${k}`} castShadow receiveShadow
                position={[(a0.x + a1.x) / 2, yc, (a0.z + a1.z) / 2]}
                rotation={[0, -Math.atan2(a1.z - a0.z, a1.x - a0.x), 0]}>
                <boxGeometry args={[len + 0.02, h, thick]} />
                {pieceMat(len, h)}
              </mesh>
            );
          }
        };
        // Cut EXACTLY the opening's own width, measured in arc length. Asking
        // whether a tessellated piece's MIDPOINT fell inside an opening snapped
        // the hole out to whole pieces — and a STRAIGHT wall is a single piece,
        // so one door erased the entire wall, taking the doors standing in it
        // out of sight along with it.
        const ranges = openings
          .map(o => {
            const c = arcAtT(o.t ?? 0.5), hw = (o.width || 0.9) / 2;
            return { lo: Math.max(0, c - hw), hi: Math.min(wallLen, c + hw), op: o };
          })
          .sort((p, q) => p.lo - q.lo);
        let cursor = 0;
        ranges.forEach((r, ri) => {
          if (r.lo > cursor) emitSpan(cursor, r.lo, H / 2, H, `s${ri}`);
          const sill = r.op.type === 'window' ? (r.op.sill ?? 0.9) : 0;
          const opH = r.op.height ?? (r.op.type === 'door' ? 2.06 : r.op.type === 'archway' ? 2.4 : 0.88);
          const top = sill + opH;
          const lo = Math.max(r.lo, cursor);
          if (sill > 0.02)      emitSpan(lo, r.hi, sill / 2, sill, `b${ri}`);
          if (top < H - 0.02)   emitSpan(lo, r.hi, (top + H) / 2, H - top, `t${ri}`);
          cursor = Math.max(cursor, r.hi);
        });
        if (cursor < wallLen) emitSpan(cursor, wallLen, H / 2, H, 'e');
        return meshes;
      })()}
    </group>
  );
}

// ─── FREE-FORM ROOM (closed free-wall loop → floor + walls) ──────────────────
// `floorMat` / `wallMat` are the room's own materials. Without them the floor
// was a flat baseColor while every rectangle room's floor showed its texture —
// the same drawn room looked nothing like its neighbours.
// Openings belonging to OTHER rooms that sit on this outline edge. A drawn room
// shares its boundary with the rooms next to it, and each of their doors is a
// real hole through that shared wall — but the drawn room only ever cut for its
// OWN openings, so its wall stayed solid and stood in front of the neighbour's
// door. Returned as { t } along this edge so they can be cut like any other.
function adjacentEdgeCuts(allRooms, room, a, b) {
  if (a.bulge) return [];                       // curved edge — no rect wall lies on it
  const horiz = Math.abs(a.y - b.y) < 1e-6;
  const vert  = Math.abs(a.x - b.x) < 1e-6;
  if (!horiz && !vert) return [];               // angled edge — same
  const span = horiz ? b.x - a.x : b.y - a.y;
  if (Math.abs(span) < 1e-6) return [];
  const lo = Math.min(horiz ? a.x : a.y, horiz ? b.x : b.y);
  const hi = Math.max(horiz ? a.x : a.y, horiz ? b.x : b.y);
  const line = horiz ? a.y : a.x;

  const out = [];
  for (const r of (allRooms || [])) {
    if (r.id === room.id) continue;
    if (r.pts && r.pts.length >= 3) continue;    // another drawn room draws its own
    const rects = (r.rects && r.rects.length) ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }];
    for (const op of (r.openings || [])) {
      if (op.absX !== undefined) continue;       // step-wall opening — different space
      const pos = op.pos ?? 0.5;
      // Where this opening sits in world cells, and which line its wall is on.
      let wx, wy;
      if (op.wall === 'top')         { wx = r.x + pos * r.w; wy = r.y; }
      else if (op.wall === 'bottom') { wx = r.x + pos * r.w; wy = r.y + r.h; }
      else if (op.wall === 'left')   { wx = r.x;             wy = r.y + pos * r.h; }
      else                           { wx = r.x + r.w;       wy = r.y + pos * r.h; }
      // Must lie ON this edge's line, and within its extent.
      if (horiz) {
        if (Math.abs(wy - line) > 0.05) continue;
        if (wx < lo - 0.01 || wx > hi + 0.01) continue;
      } else {
        if (Math.abs(wx - line) > 0.05) continue;
        if (wy < lo - 0.01 || wy > hi + 0.01) continue;
      }
      // Only cut where that room actually has floor against this line.
      const touches = rects.some(rc => horiz
        ? (Math.abs(rc.y - line) < 0.05 || Math.abs(rc.y + rc.h - line) < 0.05)
        : (Math.abs(rc.x - line) < 0.05 || Math.abs(rc.x + rc.w - line) < 0.05));
      if (!touches) continue;
      const t = ((horiz ? wx : wy) - (horiz ? a.x : a.y)) / span;
      if (t < -0.01 || t > 1.01) continue;
      out.push({ ...op, t: Math.max(0, Math.min(1, t)) });
    }
  }
  return out;
}

function FreeRoom3D({ room, H, floorColor = '#d4c4a8', wallColor = '#dcd9d4', floorMat = null, wallMat = null, allRooms = [] }) {
  const pts = room.pts || [];
  // Floor outline as a THREE.Shape in (x, -z); a -90° X rotation lays it flat so
  // shape-y → world-z. Curved edges become quadratic arcs.
  const shape = useMemo(() => {
    if (pts.length < 3) return null;
    const s = new THREE.Shape();
    s.moveTo(pts[0].x * M, -pts[0].y * M);
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      if (a.bulge) {
        const { cx, cy } = edgeControlPoint(a.x, a.y, b.x, b.y, a.bulge);
        s.quadraticCurveTo(cx * M, -cy * M, b.x * M, -b.y * M);
      } else {
        s.lineTo(b.x * M, -b.y * M);
      }
    }
    return s;
  }, [room]); // eslint-disable-line
  // Same texture pipeline the rectangle rooms use, so the floors match.
  const floorData = useMemo(() => makeFloorTexture(floorMat), [floorMat?.id, floorMat?.baseColor, floorMat?.finish]);
  const floorTex = floorData?.texture ?? null;
  if (!shape) return null;
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} receiveShadow>
        <shapeGeometry args={[shape]} />
        <meshStandardMaterial
          color={floorTex ? '#ffffff' : floorColor}
          map={floorTex}
          roughness={floorData?.roughness ?? 0.85}
          metalness={floorData?.metalness ?? 0}
          side={THREE.DoubleSide}
        />
      </mesh>
      {pts.map((a, i) => {
        const b = pts[(i + 1) % pts.length];
        // Free-form openings are keyed by outline edge + t. (Openings created by
        // the rectangle tools use wall/pos and don't apply to a curved outline.)
        const edgeOps = (room.openings || []).filter(o => o.edge === i && o.t != null);
        return (
          <group key={i}>
            <FreeWall3D wall={{ x1: a.x, y1: a.y, x2: b.x, y2: b.y, bulge: a.bulge, thickness: 0.16 }} H={H} color={wallColor} mat={wallMat}
              openings={[
                ...edgeOps.filter(o => !isWallDecorType(o.type)),
                // Cut for the neighbour's doors too — the model itself is drawn
                // by the room that owns it, we only need the hole here.
                ...adjacentEdgeCuts(allRooms, room, a, b).filter(o => !isWallDecorType(o.type)),
              ]} />
            {/* Door / window models sitting in the cut openings */}
            {edgeOps.map(o => {
              const q = edgePointAt(a, b, o.t);
              const gx = q.x * M, gz = q.y * M;
              const rotY = -q.angle;              // wall tangent → model facing
              if (o.type === 'door') {
                return (
                  <group key={o.id} position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
                    <GLBDoor styleId={GLB_DOOR_STYLES.has(o.style) ? o.style : 'door1'}
                      dW={o.width || 0.86} dH={o.height || 2.05} color={o.color || '#e8e0d4'}
                      tint={o.colored ? (o.color || null) : null} />
                  </group>
                );
              }
              if (o.type === 'window') {
                return (
                  <group key={o.id} position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
                    <GLBWindow styleId={GLB_WINDOW_STYLES.has(o.style) ? o.style : 'window1'}
                      wW={o.width || WIN_W} wH={o.height || WIN_H} wSill={o.sill ?? WIN_SILL}
                      tint={o.colored ? (o.color || null) : null} />
                  </group>
                );
              }
              return null; // archway = a plain cut (handled by FreeWall3D)
            })}
          </group>
        );
      })}
    </group>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────

export default function House3DView({ shared = false }) {
  const dispatch        = useDispatch();
  const { rooms, designs } = useSelector(s => s.house);
  const wallSegments      = useSelector(s => s.house.wallSegments || []);
  const freeWalls         = useSelector(s => s.house.freeWalls || []);
  const freeRooms         = useSelector(s => s.house.freeRooms || []);
  const autoWallOpenings  = useSelector(s => s.house.autoWallOpenings || {});
  const allFurniture      = useSelector(s => s.house.furniture);
  const wallHeight        = useSelector(s => s.house.wallHeight ?? 2.8);
  const houseState        = useSelector(s => s.house);
  const { t } = useT();
  const { user } = useAuth();
  const { projectId } = useParams();
  const isGuest = useGuestMode();
  const { markSaved, isDirty, changeToken, confirmIfDirty } = useUnsavedChanges();
  const [webSaving, setWebSaving] = useState(false);
  // Time of day — same presets as the per-room view, so the whole-house view
  // gets the identical morning/evening/night lighting, sky and mood.
  const [timeOfDay, setTimeOfDay] = useState('morning');
  const tod = TIME_OF_DAY_PRESETS[timeOfDay];
  const hl  = HOUSE_LIGHT[timeOfDay];

  // Branded loader — stays up until the house has painted AND every GLB item
  // (furniture, doors, windows, clocks) has loaded, so the user never sees a
  // half-populated house with items popping in. Same load-counter approach as
  // the per-room view: GLB items register start/done through GlbLoadingContext.
  const [sceneReady, setSceneReady] = useState(false);
  const [glbPending, setGlbPending] = useState(0);
  const glbPendingRef = useRef(0);
  const glbLoad = useMemo(() => ({
    start: () => { glbPendingRef.current += 1; setGlbPending(glbPendingRef.current); },
    done:  () => { glbPendingRef.current = Math.max(0, glbPendingRef.current - 1); setGlbPending(glbPendingRef.current); },
  }), []);
  const [loaderOn, setLoaderOn] = useState(true);
  const loadingPhaseRef = useRef(true);

  // Hide once painted AND all GLBs are in (held at 0 for a 400ms settle so a
  // momentary dip before every item registers doesn't lift it early).
  useEffect(() => {
    if (!loadingPhaseRef.current) return undefined;
    if (!sceneReady || glbPending !== 0) return undefined;
    const id = setTimeout(() => {
      if (loadingPhaseRef.current && glbPendingRef.current === 0) {
        loadingPhaseRef.current = false;
        setLoaderOn(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [sceneReady, glbPending]);

  // Safety net: never let a hung/dead asset pin the overlay open (each GLB item
  // also self-completes after 25s).
  useEffect(() => {
    const id = setTimeout(() => {
      if (loadingPhaseRef.current) { loadingPhaseRef.current = false; setLoaderOn(false); }
    }, 35000);
    return () => clearTimeout(id);
  }, []);

  async function handleWebSave() {
    if (!projectId || !user) return;
    setWebSaving(true);
    const s = houseState;
    const sceneData = JSON.stringify({
      version: FILE_VERSION, savedAt: new Date().toISOString(),
      phase: s.phase, rooms: s.rooms, designs: s.designs, furniture: s.furniture, lighting: s.lighting || [],
      wallSegments: s.wallSegments || [], freeWalls: s.freeWalls || [], nextFreeWallId: s.nextFreeWallId || 1,
      freeRooms: s.freeRooms || [], nextFreeRoomId: s.nextFreeRoomId || 1,
      autoWallOpenings: s.autoWallOpenings || {},
      wallHeight: s.wallHeight ?? 2.8, nextId: s.nextId,
      nextFurnitureId: s.nextFurnitureId, nextLightingId: s.nextLightingId || 1, nextOpeningId: s.nextOpeningId,
      nextWallId: s.nextWallId || 1, nextWallOpeningId: s.nextWallOpeningId || 1,
    });
    try {
      await saveProjectToWeb(projectId, sceneData);
      markSaved();
    } catch (err) {
      console.error('Web save failed:', err);
    } finally {
      setWebSaving(false);
    }
  }

  // Autosave to the cloud shortly after the user stops editing.
  useAutoSave({
    isDirty, changeToken, save: handleWebSave,
    enabled: !!projectId && !!user,
  });
  const H               = wallHeight;
  const furnitureByRoom = useMemo(() => {
    const map = {};
    for (const room of rooms) {
      const isCompound = room.rects && room.rects.length > 1;
      map[room.id] = allFurniture.filter(item => {
        const rot = item.rotation || 0;
        const dw  = rot % 180 === 0 ? item.w : item.h;
        const dh  = rot % 180 === 0 ? item.h : item.w;
        const cx  = item.x + dw / 2, cy = item.y + dh / 2;
        if (isCompound) {
          return room.rects.some(r => {
            const rl = r.x * CELL, rt = r.y * CELL;
            const rr = (r.x + r.w) * CELL, rb = (r.y + r.h) * CELL;
            return cx >= rl && cx <= rr && cy >= rt && cy <= rb;
          });
        }
        const left   = room.x * CELL, top    = room.y * CELL;
        const right  = (room.x + room.w) * CELL, bottom = (room.y + room.h) * CELL;
        return cx >= left && cx <= right && cy >= top && cy <= bottom;
      });
    }
    return map;
  }, [allFurniture, rooms]);

  // Inject auto-wall openings into rooms. Each opening is assigned to exactly ONE room
  // (the first match in rooms array order) so interior-wall openings don't render twice
  // facing opposite directions and create a dark artifact.
  const augmentedRooms = useMemo(() => {
    const keys = Object.keys(autoWallOpenings);
    if (!keys.length) return rooms;

    const extrasByIdx = rooms.map(() => []);

    for (const key of keys) {
      const ops = autoWallOpenings[key];
      if (!ops || !ops.length) continue;
      const parts = key.split(':');

      for (const op of ops) {
        let assigned = false;
        if (parts[0] === 'h') {
          const Y = Number(parts[1]), X1 = Number(parts[2]), X2 = Number(parts[3]);
          const autoLen = X2 - X1;
          const worldX = X1 + op.t * autoLen;
          for (let i = 0; i < rooms.length && !assigned; i++) {
            const r = rooms[i];
            let side = null;
            if (r.y + r.h === Y && X1 < r.x + r.w && X2 > r.x) side = 'bottom';
            else if (r.y === Y  && X1 < r.x + r.w && X2 > r.x) side = 'top';
            if (side && worldX >= r.x - 0.01 && worldX <= r.x + r.w + 0.01) {
              extrasByIdx[i].push({ ...op, wall: side, pos: (worldX - r.x) / r.w });
              assigned = true;
            }
          }
        } else if (parts[0] === 'v') {
          const X = Number(parts[1]), Y1 = Number(parts[2]), Y2 = Number(parts[3]);
          const autoLen = Y2 - Y1;
          const worldY = Y1 + op.t * autoLen;
          for (let i = 0; i < rooms.length && !assigned; i++) {
            const r = rooms[i];
            let side = null;
            if (r.x + r.w === X && Y1 < r.y + r.h && Y2 > r.y) side = 'right';
            else if (r.x === X  && Y1 < r.y + r.h && Y2 > r.y) side = 'left';
            if (side && worldY >= r.y - 0.01 && worldY <= r.y + r.h + 0.01) {
              extrasByIdx[i].push({ ...op, wall: side, pos: (worldY - r.y) / r.h });
              assigned = true;
            }
          }
        }
      }
    }

    return rooms.map((room, i) => {
      if (!extrasByIdx[i].length) return room;
      const ownOps = room.openings || [];
      const extras = extrasByIdx[i].filter(extra =>
        !ownOps.some(op => op.wall === extra.wall && Math.abs(op.pos - extra.pos) < 0.08)
      );
      if (!extras.length) return room;
      // DEDUPED AGAINST EACH OTHER TOO. The filter above only removes extras
      // that clash with the room's OWN openings; two neighbours sharing a wall
      // could still contribute the same opening twice, which is what put two
      // children under one key in the render below.
      const seen = new Set();
      const uniqueExtras = extras.filter((e) => {
        const k = `${e.id}:${e.wall}:${e.pos}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      return { ...room, openings: [...ownOps, ...uniqueExtras] };
    });
  }, [rooms, autoWallOpenings]);

  const containerRef = useRef(null);

  // The Download button now opens a chooser instead of saving straight away.
  // The capture itself still happens at click time inside the modal, against the
  // live canvas — a WebGL drawing buffer can be cleared between frames, so a
  // data URL grabbed when the modal opened could be blank by the time it is used.
  const [dlOpen, setDlOpen] = useState(false);
  const captureHouse = useCallback(() => {
    const canvas = containerRef.current?.querySelector('canvas');
    return canvas ? watermarkCanvasToDataUrl(canvas) : null;
  }, []);
  const handleDownloadChoice = useCallback((r) => {
    if (r.type === 'save') saveOrShareImage(r.dataUrl, r.filename, t('full_house'));
  }, [t]);
  function handleScreenshot() { setDlOpen(true); }

  const cam = useMemo(() => {
    const hasRooms = rooms.length > 0;
    const hasSegs  = wallSegments.length > 0;
    const fwPts    = [...freeWalls, ...freeRooms.flatMap(r => (r.pts || []).map((p, i) => ({ x1: p.x, y1: p.y, x2: p.x, y2: p.y })))];
    const hasFree  = fwPts.length > 0;
    if (!hasRooms && !hasSegs && !hasFree) return { pos: [8, 10, 8], target: [0, H * 0.3, 0], maxDist: 40 };
    const xs = [
      ...(hasRooms ? rooms.flatMap(r => [r.x, r.x + r.w]) : []),
      ...(hasSegs  ? wallSegments.flatMap(s => [s.x1, s.x2]) : []),
      ...(hasFree  ? fwPts.flatMap(s => [s.x1, s.x2]) : []),
    ];
    const zs = [
      ...(hasRooms ? rooms.flatMap(r => [r.y, r.y + r.h]) : []),
      ...(hasSegs  ? wallSegments.flatMap(s => [s.y1, s.y2]) : []),
      ...(hasFree  ? fwPts.flatMap(s => [s.y1, s.y2]) : []),
    ];
    const minX = Math.min(...xs) * M, maxX = Math.max(...xs) * M;
    const minZ = Math.min(...zs) * M, maxZ = Math.max(...zs) * M;
    const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
    const span = Math.max(maxX - minX, maxZ - minZ, 4);
    return {
      pos:     [cx + span * 1.0, span * 0.95, cz + span * 1.0],
      target:  [cx, H * 0.3, cz],
      maxDist: span * 2.8,
    };
  }, [rooms, wallSegments, freeWalls, freeRooms, H]);

  return (
    <div ref={containerRef} className="house3d-page">
      <Loader3D visible={loaderOn} label={t('loading_3d')} />

      {/* Time-of-day switch — same morning/evening/night as the per-room view.
          Floated below the global app header (which overlays the top of this
          fixed page) so it's always visible and clickable. */}
      <div style={{
        position: 'absolute', top: 'calc(var(--header-h, 56px) + 12px)', left: '50%',
        transform: 'translateX(-50%)', zIndex: 15,
        display: 'flex', gap: 4, padding: 4, borderRadius: 12,
        background: 'rgba(22,24,34,0.86)', border: '1px solid rgba(255,255,255,0.12)',
        backdropFilter: 'blur(10px)', boxShadow: '0 4px 18px rgba(0,0,0,0.4)',
      }}>
        {Object.keys(TIME_OF_DAY_PRESETS).map((key) => (
          <button
            key={key}
            onClick={() => setTimeOfDay(key)}
            title={t('time_' + key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: timeOfDay === key ? '6px 13px' : '6px 9px',
              borderRadius: 8, border: 'none', cursor: 'pointer',
              background: timeOfDay === key ? 'rgba(255,200,80,0.95)' : 'transparent',
              color: timeOfDay === key ? '#1a1400' : '#cfd2e0',
              fontWeight: 700, fontSize: 12.5, transition: 'all 0.15s',
            }}
          >
            <ItemIcon type={key} size={15} />
            {timeOfDay === key && <span>{t('time_' + key)}</span>}
          </button>
        ))}
      </div>
      <div className="house3d-header">
        {/* No "back to plan" for a shared read-only viewer — there's no editor
            to go back to. The shared page supplies its own header instead. */}
        {!shared && (
          <button className="house3d-back-btn" onClick={() => confirmIfDirty(() => dispatch(goToPhase('plan')))}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2.5L4.5 7L9 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <Tx k="floor_plan_back" />
          </button>
        )}
        <div className="house3d-title">
          <span className="house3d-eyebrow">{t('preview_3d')}</span>
          <span className="house3d-name">{t('full_house')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="house3d-hint">{t('drag_hint_3d')}</span>
          {!isGuest && (
            <button onClick={handleScreenshot} title={t('screenshot')} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 20, color: '#d4c88a', fontWeight: 600, fontSize: 12,
              cursor: 'pointer', padding: '6px 12px',
            }}>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M1 4.5C1 3.67 1.67 3 2.5 3H4L5 1.5H9L10 3H11.5C12.33 3 13 3.67 13 4.5V10.5C13 11.33 12.33 12 11.5 12H2.5C1.67 12 1 11.33 1 10.5V4.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                <circle cx="7" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
              <Tx k="screenshot" />
            </button>
          )}
          {shared ? null : isGuest ? (
            <GuestSaveButton />
          ) : (
            <div
              className={`h3d-save-btn h3d-save-btn--status${(webSaving || isDirty) ? ' h3d-save-btn--saving' : ''}`}
              title={projectId ? 'Autosaves to the cloud' : 'Open from Workspace to enable autosave'}
            >
              {(webSaving || isDirty) ? (
                <span className="h3d-save-spinner" />
              ) : (
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                  <rect x="3.5" y="1.5" width="5" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
                  <rect x="3" y="7" width="8" height="4.5" rx="0.8" stroke="currentColor" strokeWidth="1.2"/>
                  <line x1="8.5" y1="2.5" x2="8.5" y2="4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              )}
              {(webSaving || isDirty) ? t('saving') : t('saved')}
            </div>
          )}
        </div>
      </div>

      <Canvas
        shadows={{ type: THREE.PCFShadowMap }}
        dpr={[1, 2]}
        // Same GPU budget as the room view — the dollhouse draws the WHOLE
        // home at once, so if either view is going to exhaust a phone's memory
        // and get the tab killed, it is this one. See utils/gpuProfile.
        gl={{
          antialias: useMSAA(),
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.05,
          // Stays ON - see the note in ThreeDRoom. The screenshot and AI
          // render both read this canvas after the fact.
          preserveDrawingBuffer: true,
        }}
        style={{ width: '100%', height: '100%' }}
        onCreated={(state) => {
          // A lost context is only recoverable if we preventDefault here —
          // otherwise the browser abandons the canvas and the page appears to
          // reload itself. The room view already did this; this view did not.
          const canvas = state.gl.domElement;
          canvas.addEventListener('webglcontextlost', (e) => {
            e.preventDefault();
            console.warn('[house3d] WebGL context lost — asked the browser to restore it');
          }, false);
          canvas.addEventListener('webglcontextrestored', () => {
            console.warn('[house3d] WebGL context restored');
          }, false);
        }}
      >
        <GlbLoadingContext.Provider value={glbLoad}>
        <SceneReadySignal onReady={() => setSceneReady(true)} />
        <PerspectiveCamera makeDefault fov={52} position={cam.pos} near={0.1} far={600} />
        <OrbitControls
          target={cam.target}
          minPolarAngle={0.08}
          maxPolarAngle={Math.PI * 0.52}
          minDistance={2}
          maxDistance={cam.maxDist}
          enableDamping dampingFactor={0.08}
        />

        <ExposureControl exposure={tod.exposure} />
        {/* Sky + lighting driven by the shared time-of-day presets so the whole
            house reads the same morning/evening/night as the per-room view. The
            light INTENSITIES come from HOUSE_LIGHT (tuned lower — a dollhouse
            lights far more surface than one room, so the room presets would blow
            the walls out). */}
        <color attach="background" args={[tod.bg]} />
        {/* Neutral env preset (not the tod dawn/sunset HDRIs) — those cast a
            strong pink/orange tint across the whole open dollhouse. Reflections
            only; the morning/evening/night mood comes from the sun/ambient/
            exposure/bg below. */}
        {/* Wrapped: the preset is fetched from a rate-limited CDN and a 429
            was taking the whole app down. See SafeEnvironment. */}
        <SafeEnvironment preset="apartment" background={false} environmentIntensity={hl.env} />
        {/* Neutral sky fill — a saturated sky colour here (e.g. the light-blue
            morning bg) mixes with the warm sun/ambient and turns grey walls
            magenta, so keep this desaturated and let bg/sun set the mood. */}
        <hemisphereLight skyColor="#ece8e0" groundColor="#33352c" intensity={hl.hemi} />
        <ambientLight intensity={hl.amb} color={tod.ambientColor} />
        <directionalLight
          position={[0, 40, 0]} intensity={hl.sun} color={tod.sunColor} castShadow
          shadow-mapSize-width={SHADOW_MAP} shadow-mapSize-height={SHADOW_MAP} shadow-bias={-0.0006}
        />
        <directionalLight position={[-14, 18, -14]} intensity={hl.fill} color="#dfe7ff" />

        {/* Ground — painted the sky colour (unlit) so the surround blends
            seamlessly into the background, matching the per-room view where the
            house simply floats against the sky rather than sitting on a yard. */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cam.target[0], -0.01, cam.target[2]]}>
          <planeGeometry args={[300, 300]} />
          <meshBasicMaterial color={tod.bg} />
        </mesh>

        {(() => {
          // Expand compound rooms into per-rect virtual rooms.
          // Compute the weighted centroid of each compound room for label placement.
          // Free-form rooms carry a smooth outline (`pts`). Rendering them via
          // their rasterised rects would produce staircase walls, so they're
          // drawn by FreeRoom3D (curved floor + walls) instead of being expanded.
          const ptsRooms = augmentedRooms.filter(r => r.pts && r.pts.length >= 3);
          // A drawn room draws its own walls, so a rect room next to one must not
          // draw a duplicate over them. Its rasterised cells stand in as the
          // neighbour the rectangle split can't see.
          const drawnBlockers = ptsRooms.flatMap(r =>
            (r.rects && r.rects.length ? r.rects : [{ x: r.x, y: r.y, w: r.w, h: r.h }])
              .map((rc, i) => ({ ...rc, id: `d${r.id}_${i}`, _drawnRoom: true })));
          const expandedRooms = augmentedRooms.filter(r => !(r.pts && r.pts.length >= 3)).flatMap(room => {
            if (!room.rects || room.rects.length <= 1) return [{ ...room, labelPos: null }];
            let totalArea = 0, lcx = 0, lcz = 0;
            for (const r of room.rects) {
              const area = r.w * r.h;
              lcx += (r.x + r.w / 2) * area;
              lcz += (r.y + r.h / 2) * area;
              totalArea += area;
            }
            const labelPos = totalArea > 0 ? [(lcx / totalArea) * M, (lcz / totalArea) * M] : null;

            // Compound BB in metres (pos for BB-edge openings is a fraction of THIS)
            const origRx = room.x * M, origRz = room.y * M;
            const origW  = room.w * M, origD  = room.h * M;

            // Pre-assign each BB-edge opening to the virtual rect whose wall it lies on.
            // Remap pos to be relative to that rect's wall so wallPieces + rendering stay correct.
            const openingAssignments = new Map(); // op.id → { rectIdx, remappedPos }
            for (const op of (room.openings || [])) {
              if (op.absX !== undefined) continue; // step-wall: handled separately
              let assigned = false;
              for (let ri = 0; ri < room.rects.length; ri++) {
                const rc   = room.rects[ri];
                const rcRx = rc.x * M, rcRz = rc.y * M;
                const rcW  = rc.w * M, rcD  = rc.h * M;
                let match = false, rpos;
                if (op.wall === 'top') {
                  const wx = origRx + op.pos * origW;
                  if (Math.abs(rcRz - origRz) < 0.05 && wx >= rcRx - 0.05 && wx <= rcRx + rcW + 0.05) {
                    match = true; rpos = Math.max(0, Math.min(1, (wx - rcRx) / rcW));
                  }
                } else if (op.wall === 'bottom') {
                  const wx = origRx + op.pos * origW;
                  if (Math.abs(rcRz + rcD - (origRz + origD)) < 0.05 && wx >= rcRx - 0.05 && wx <= rcRx + rcW + 0.05) {
                    match = true; rpos = Math.max(0, Math.min(1, (wx - rcRx) / rcW));
                  }
                } else if (op.wall === 'left') {
                  const wz = origRz + op.pos * origD;
                  if (Math.abs(rcRx - origRx) < 0.05 && wz >= rcRz - 0.05 && wz <= rcRz + rcD + 0.05) {
                    match = true; rpos = Math.max(0, Math.min(1, (wz - rcRz) / rcD));
                  }
                } else { // right
                  const wz = origRz + op.pos * origD;
                  if (Math.abs(rcRx + rcW - (origRx + origW)) < 0.05 && wz >= rcRz - 0.05 && wz <= rcRz + rcD + 0.05) {
                    match = true; rpos = Math.max(0, Math.min(1, (wz - rcRz) / rcD));
                  }
                }
                if (match) { openingAssignments.set(op.id, { rectIdx: ri, remappedPos: rpos }); assigned = true; break; }
              }
              if (!assigned) openingAssignments.set(op.id, { rectIdx: 0, remappedPos: op.pos });
            }

            return room.rects.map((rect, i) => {
              const rectOpenings = (room.openings || []).reduce((acc, op) => {
                if (op.absX !== undefined) {
                  // Step-wall openings: rect_0 renders the 3D model; wall-cutting uses stepOpsAsPos
                  if (i === 0) acc.push(op);
                } else {
                  const asgn = openingAssignments.get(op.id);
                  if (asgn && asgn.rectIdx === i) acc.push({ ...op, pos: asgn.remappedPos });
                }
                return acc;
              }, []);
              return {
                ...room,
                id: `${room.id}_${i}`,
                x: rect.x, y: rect.y, w: rect.w, h: rect.h,
                rects: [rect],
                openings: rectOpenings,
                labelPos: i === 0 ? labelPos : null,
                origX: room.x, origY: room.y,
              };
            });
          });

          return (
            <>
              {expandedRooms.map(vr => {
                const origId = getOrigId(vr.id);
                const isFirst = typeof vr.id === 'string' ? vr.id.endsWith('_0') : true;
                return (
                  <RoomBox
                    key={String(vr.id)}
                    room={vr}
                    design={designs[origId] || designs[vr.id]}
                    furniture={isFirst ? (furnitureByRoom[origId] || []) : []}
                    allRooms={expandedRooms}
                    drawnBlockers={drawnBlockers}
                    H={H}
                    showLabel={isFirst}
                    labelPos={vr.labelPos}
                    timeOfDay={timeOfDay}
                  />
                );
              })}
              {/* Free-form rooms (angled / curved outline) — smooth floor + walls,
                  using the room's own design materials, plus its furniture. */}
              {ptsRooms.map(room => {
                const dz = designs[getOrigId(room.id)] || designs[room.id];
                const fm = resolveMat(dz?.floor, FLOOR_MATERIALS);
                const wm = resolveMat(dz?.wall, WALL_MATERIALS);
                return (
                  <group key={`ptsr-${room.id}`}>
                    <FreeRoom3D room={room} H={H}
                      floorColor={fm?.baseColor || '#d4c4a8'}
                      wallColor={wm?.color || '#f0e8d8'}
                      floorMat={fm} wallMat={wm} allRooms={augmentedRooms} />
                    {/* Deduped for the same reason as in RoomWorld above: a
                        room split into pieces can collect one item more than
                        once. */}
                    {(furnitureByRoom[getOrigId(room.id)] || [])
                      .filter((item, i, all) => all.findIndex(o => o.id === item.id) === i)
                      .map(item => (
                        <FurnitureWorld key={item.id} item={item} timeOfDay={timeOfDay} />
                      ))}
                    <pointLight position={[(room.x + room.w / 2) * M, H * 0.92, (room.y + room.h / 2) * M]}
                      intensity={HOUSE_LIGHT[timeOfDay].pt} color="#fff3e0"
                      distance={Math.max(room.w, room.h) * M * 2.6} decay={2} />
                  </group>
                );
              })}

              {/* Compound (merged / non-rectangular) rooms paint their walls under
                  per-segment keys the rectangle split can't reach — render those
                  face materials on the true perimeter geometry here.
                  A DRAWN room's outline rasterises into many rects too, so it
                  matched this filter — but it's already fully rendered above by
                  FreeRoom3D, addressed by outline edge (fe_N), not by this rect
                  tracer's h_top_x_y/v_left_x_y keys. Running it anyway traced a
                  stair-stepped rectangular approximation of the curve and laid
                  any paint that happened to match one of ITS keys as an opaque
                  panel over the real (curved, correctly painted) wall — which is
                  exactly what was hiding a door standing right there. */}
              {augmentedRooms.filter(r => r.rects && r.rects.length > 1 && !(r.pts && r.pts.length >= 3)).map(room => {
                const origId = getOrigId(room.id);
                return (
                  <CompoundWallOverlays
                    key={`cwo-${String(room.id)}`}
                    room={room}
                    design={designs[origId] || designs[room.id]}
                    H={H}
                  />
                );
              })}
            </>
          );
        })()}

        {/* Standalone wall segments drawn via Draw Walls mode */}
        {wallSegments.map(seg => (
          <WallSegment3D key={seg.id} seg={seg} H={H} wallColor={segWallColor(seg, rooms, designs)} mat={segWallMaterial(seg, rooms, designs)} iwPatches={segIwPatches(seg, designs)} />
        ))}

        {/* Free-form walls (any angle + curve), with their openings cut out and
            the door/window models standing in them — same as a room's own walls. */}
        {freeWalls.map(w => {
          // Decor mounts on the face; only structural openings cut the wall.
          const ops = (w.openings || []).filter(o => !isWallDecorType(o.type));
          return (
            <group key={`fw-${w.id}`}>
              <FreeWall3D wall={w} H={H} color={segWallColor(w, rooms, designs)} mat={segWallMaterial(w, rooms, designs)} openings={ops} />
              {ops.map(o => {
                const q = edgePointAt({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 }, o.t ?? 0.5);
                const pos = [q.x * M, 0, q.y * M];
                const rotY = -q.angle;              // wall tangent → model facing
                if (o.type === 'door') {
                  return (
                    <group key={o.id} position={pos} rotation={[0, rotY, 0]}>
                      <GLBDoor styleId={GLB_DOOR_STYLES.has(o.style) ? o.style : 'door1'}
                        dW={o.width || 0.86} dH={o.height || 2.05} color={o.color || '#e8e0d4'}
                        tint={o.colored ? (o.color || null) : null} />
                    </group>
                  );
                }
                if (o.type === 'window') {
                  return (
                    <group key={o.id} position={pos} rotation={[0, rotY, 0]}>
                      <GLBWindow styleId={GLB_WINDOW_STYLES.has(o.style) ? o.style : 'window1'}
                        wW={o.width || WIN_W} wH={o.height || WIN_H} wSill={o.sill ?? WIN_SILL}
                        tint={o.colored ? (o.color || null) : null} />
                    </group>
                  );
                }
                return null;   // archway = a plain cut, already handled by FreeWall3D
              })}
            </group>
          );
        })}

        {/* Free-form rooms (closed loop → floor + walls) */}
        {freeRooms.map(r => {
          const fm = resolveMat(r.floor, FLOOR_MATERIALS);
          const wm = resolveMat(r.wall, WALL_MATERIALS);
          return <FreeRoom3D key={`fr-${r.id}`} room={r} H={H} floorColor={fm?.baseColor || '#d4c4a8'} wallColor={wm?.color || '#dcd9d4'} floorMat={fm} wallMat={wm} />;
        })}
        </GlbLoadingContext.Provider>
      </Canvas>

      <DownloadModal
        open={dlOpen}
        onClose={() => setDlOpen(false)}
        capture={captureHouse}
        canvasRef={() => containerRef.current?.querySelector('canvas')}
        filename="full-house-3d.png"
        onNotify={handleDownloadChoice}
        timeOfDay={timeOfDay}
      />

      {/* Bottom-center back button (hidden for shared read-only viewers) */}
      {!shared && (
        <button className="house3d-bottom-back" onClick={() => confirmIfDirty(() => dispatch(goToPhase('plan')))}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2.5L4.5 7L9 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <Tx k="floor_plan_back" />
        </button>
      )}

      {/* Bottom-right screenshot button — thumb-reachable duplicate of the
          header one, for mobile where the header row can be easy to miss. */}
      {!isGuest && (
        <button className="house3d-bottom-screenshot" onClick={handleScreenshot} title={t('screenshot')}>
          <svg width="15" height="15" viewBox="0 0 14 14" fill="none">
            <path d="M1 4.5C1 3.67 1.67 3 2.5 3H4L5 1.5H9L10 3H11.5C12.33 3 13 3.67 13 4.5V10.5C13 11.33 12.33 12 11.5 12H2.5C1.67 12 1 11.33 1 10.5V4.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
            <circle cx="7" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.2"/>
          </svg>
        </button>
      )}

    </div>
  );
}
